/* eslint-disable react-refresh/only-export-components */
import React from "react";
import moment from "moment";

import IconButton from "@mui/material/IconButton";
import AccordionDetails from "@mui/material/AccordionDetails";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";

import CustomButtonGroup from "../../../components/common/CustomButtonGroup";
import CustomSelect from "../../../components/common/CustomSelect";
import CustomInput from "../../../components/common/CustomInput";
import CustomDatePicker from "../../../components/common/CustomDatePicker";
import CustomAutoComplete from "../../../components/common/CustomAutoComplete";
import CustomRadio from "../../../components/common/CustomRadio";
import CheckBox from "../../../components/common/CheckBox";
import CustomChip from "../../../components/common/CustomChip";

import ShareIcon from "../../../assets/ShareIcon.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AuditTag from "../../../components/common/AuditTag";
import AccordionDownArrow from "../../../assets/AccordionDownArrow.svg";

import { ObjectType } from "../../../types/tableTypes";
import { DEFAULT_AUDIT_COMMENT, getAuditFieldPath } from "../../../constants/auditFields";
import {
  LocationSuggestion,
  ZipCodeSuggestion,
} from "../../../types/fieldTypes";

import { isFalsyButNotZero } from "../../../utils/Validations";
import {
  evaluation,
  evaluationTestFieldTypes,
  getCookie,
  isWithinLast6Months,
  LoanSizerEnum,
  normalizeEvaluationResult,
  normalizeReqBody,
  normalizeResultLoanData,
  parseTemplateLabel,
} from "../../../utils/Common";
import {
  getCriticalTests,
  getEligibleResult,
  getGuidlineEvaluationResult,
  getTemplateResponse,
  getTemplateResponseDSCR,
} from "../../../services/network";
import { Tooltip } from "@mui/material";
import { SizingType } from "../../../types/enums";
import { dscrArmFields } from "./LoanFieldsData";

export const sampleCondoEligibility = {
  monthlyHOAFee: null,
  hoaDuesAboveFifteen: null,
  hoaDuesAboveTwenty: null,
  reserveFundsForOneYear: null,
  reserveFundsForTwoYears: null,
  isProjectCompleted: null,
  subjectToAdditionalPhasing: null,
  soldPercentage: null,
  hoaUnderOwnerControl: null,
  simpleEstateOwnership: null,
  ownerOccupiedUnits: null,
  renterOccupiedUnits: null,
  renterOccupiedPercentage: null,
  individualOwnershipAboveTwentyFive: null,
  masterAssurancePolicy: null,
  projectHasMobileHomes: null,
  otherSourcesIncomeMoreThanTwenty: null,
  commercialPurposesAreaMoreThanForty: null,
  restrictOwnerToRent: null,
  documentsWithSEC: null,
  hoawithLitOrBankruptcy: null,
  ownerControlStartDate: null,
  indivdualOwnedUnits: null,
  hoawithLitOrBankruptcyDesc: null,
  restrictOwnerToRentOutDesc: null,
  condoProjectName: null,
  streetAddressLine1: null,
  noOfUnits: null,
  state: null,
  city: null,
  postalCode: null,
};

export const postGuidelineEvaluation: any = async (
  setDebouncedValue: any,
  tempLPData?: any,
) => {
  setDebouncedValue(tempLPData);
};

const getForeignNationalValue = (value: string) => {
  if (value === "No") {
    return "N";
  }
  if (value === "Yes") {
    return "Y";
  }
  return value;
};

const EXCLUDED_TEST_NAMES = [
  "sectionEligibility",
  "overallLoanEligibility",
  "criticalTestLoanEligibility",
];

type TestOutcome = {
  outcome: "FAIL" | "WAIVED" | "PASS" | null;
  testData: any;
};

// Builds the pass/fail summary for a single DSCR-partner test. Extracted out
// of getTestTabResultData's per-partner forEach so that loop only has to
// dispatch on the outcome (push to the right array / bump the fail count)
// instead of also inlining the testType lookup, evaluation() call, and
// testData construction.
function buildDscrPartnerTestResult(
  testName: string,
  tests: ObjectType,
  logicTests: ObjectType,
  maxMinTests: ObjectType,
  templateResponse: ObjectType,
  key: string,
  currentPartnerLoanResult: ObjectType,
  templateLabels: any,
  criticalTestData: ObjectType,
  loanResultInputForEval: ObjectType,
): TestOutcome | null {
  if (EXCLUDED_TEST_NAMES.includes(testName)) {
    return null;
  }

  const testType = logicTests[testName]
    ? `logicTests`
    : maxMinTests[testName]
      ? `maxMinParameters`
      : `finalCalculation`;
  const testFailReason = evaluation(
    templateResponse[key]?.data?.[
      `loanResult.${testType}.${testName}.message.FAIL`
    ],
    {
      ...currentPartnerLoanResult,
      loanResult: {
        ...currentPartnerLoanResult.loanResult,
        afterAdjustmentMaxLTVAsIs:
          currentPartnerLoanResult?.["toorakProceeds"]?.[
            "afterAdjustmentMaxLTVAsIs"
          ] ??
          currentPartnerLoanResult?.["toorakProceeds"]?.[
            "toorakAdjustedAsIsLtv"
          ],
        toorakAdjustedAsIsLtv:
          currentPartnerLoanResult?.["toorakProceeds"]?.[
            "afterAdjustmentMaxLTVAsIs"
          ] ??
          currentPartnerLoanResult?.["toorakProceeds"]?.[
            "toorakAdjustedAsIsLtv"
          ],
        dscr: currentPartnerLoanResult?.["loanEconomics"]?.["dscr"],
        logicTests:
          currentPartnerLoanResult?.logicTests ??
          currentPartnerLoanResult?.loanResult?.logicTests,
        ...loanResultInputForEval,
      },
    },
    templateLabels,
  );
  const testData = {
    title:
      parseTemplateLabel(
        templateResponse[key]?.data?.[`testNames.${testName}`],
      ) ?? testName,
    status: "Failed",
    subText: testFailReason,
    isCriticalTest: criticalTestData?.[key]?.includes(testName) ?? false,
    testType,
    testKey: testName,
  };

  const upperStatus = String(tests[testName]).toUpperCase();
  if (upperStatus === "FAIL" || upperStatus === "WAIVED") {
    return { outcome: upperStatus, testData };
  }
  if (upperStatus === "PASS") {
    return { outcome: "PASS", testData };
  }
  return { outcome: null, testData };
}

// Classifies one DSCR-partner test and pushes it into the right array.
// Returns 1 when it should count towards failedCountTests (only when there
// is no assignedPartner yet, matching the original inline logic), else 0.
// Extracted (together with buildDscrPartnerTestResult above) so the
// forEach callback in getTestTabResultData is a single call expression
// instead of nested if/switch statements four function-scopes deep.
function applyDscrTestOutcome(
  testName: string,
  tests: ObjectType,
  logicTests: ObjectType,
  maxMinTests: ObjectType,
  templateResponse: ObjectType,
  key: string,
  currentPartnerLoanResult: ObjectType,
  templateLabels: any,
  criticalTestData: ObjectType,
  loanResultInputForEval: ObjectType,
  tempFailedArr: ObjectType[],
  passTests: any[],
  assignedPartner: string | undefined,
): number {
  const testResult = buildDscrPartnerTestResult(
    testName,
    tests,
    logicTests,
    maxMinTests,
    templateResponse,
    key,
    currentPartnerLoanResult,
    templateLabels,
    criticalTestData,
    loanResultInputForEval,
  );
  if (!testResult) {
    return 0;
  }
  if (testResult.outcome === "FAIL" || testResult.outcome === "WAIVED") {
    tempFailedArr.push(testResult.testData);
    return assignedPartner ? 0 : 1;
  }
  if (testResult.outcome === "PASS") {
    passTests.push(testResult.testData);
  }
  return 0;
}

// Builds the pass/fail summary for a single non-DSCR (guideline) test.
// Extracted for the same reason as buildDscrPartnerTestResult above.
function buildGuidelineTestResult(
  testName: string,
  tests: ObjectType,
  guidelineTests: ObjectType,
  loanEconomicsTests: ObjectType,
  templateResponse: ObjectType,
  data: ObjectType,
  loanKey: string,
  loanDetails: ObjectType,
  fetchData: ObjectType,
  templateLabels: any,
): TestOutcome | null {
  if (!tests[testName] || testName === "sectionEligibility") {
    return null;
  }

  const testType = guidelineTests[testName]
    ? `guidelineTest`
    : loanEconomicsTests[testName]
      ? `loanEconomics`
      : ``;
  const loanResultInputForEval: any = {};
  if (data?.loanResult?.[loanKey]?.testFailureMessages) {
    Object.keys(data?.loanResult?.[loanKey].testFailureMessages).forEach(
      (key) => {
        loanResultInputForEval[key] =
          data?.loanResult?.[loanKey].testFailureMessages[key];
      },
    );
  }
  const testFailReason = evaluation(
    templateResponse?.data?.[
      `loanResult.${testType}.${testName}.message.FAIL`
    ] || evaluationTestFieldTypes[testType][testName]?.readableName,
    {
      ...data?.loanResult?.[loanKey],
      loanResult: {
        isForeignNational:
          getForeignNationalValue(
            loanDetails?.borrowerInformation?.borrowerGuarantor ?? "",
          ) ?? null,
        customerType:
          data?.loanResult?.[loanKey]?.loanCharacterisations?.customerType ??
          "",
        ...loanResultInputForEval,
      },
      loan: {
        loanSizerType:
          fetchData?.loanType === "Ground Up"
            ? LoanSizerEnum.groundUp
            : LoanSizerEnum.oneFourFamily,
      },
    },
    templateLabels,
  );
  const testData = {
    title:
      parseTemplateLabel(templateResponse?.data?.[`testNames.${testName}`]) ??
      testName,
    status: "Failed",
    subText: testFailReason,
    testType,
    testKey: testName,
  };

  const upperStatus = String(tests[testName]).toUpperCase();
  if (upperStatus === "FAIL" || upperStatus === "WAIVED") {
    return { outcome: upperStatus, testData };
  }
  if (upperStatus === "PASS") {
    return { outcome: "PASS", testData };
  }
  return { outcome: null, testData };
}

// Classifies one guideline test and pushes it into the right array. Returns
// 1 when it should count towards failedCountTests, else 0. Extracted for
// the same reason as applyDscrTestOutcome above.
function applyGuidelineTestOutcome(
  testName: string,
  tests: ObjectType,
  guidelineTests: ObjectType,
  loanEconomicsTests: ObjectType,
  templateResponse: ObjectType,
  data: ObjectType,
  loanKey: string,
  loanDetails: ObjectType,
  fetchData: ObjectType,
  templateLabels: any,
  failTests: any[],
  passTests: any[],
): number {
  const testResult = buildGuidelineTestResult(
    testName,
    tests,
    guidelineTests,
    loanEconomicsTests,
    templateResponse,
    data,
    loanKey,
    loanDetails,
    fetchData,
    templateLabels,
  );
  if (!testResult) {
    return 0;
  }
  if (testResult.outcome === "FAIL" || testResult.outcome === "WAIVED") {
    failTests.push(testResult.testData);
    return 1;
  }
  if (testResult.outcome === "PASS") {
    passTests.push(testResult.testData);
  }
  return 0;
}

// Flattens a testFailureMessages map (if present) into a plain object,
// used to spread extra fields into the evaluation() input. Extracted so
// callers don't have to inline the guard + forEach.
function buildTestFailureMessagesMap(
  testFailureMessages: ObjectType | undefined,
): ObjectType {
  const loanResultInputForEval: ObjectType = {};
  if (testFailureMessages) {
    Object.keys(testFailureMessages).forEach((key) => {
      loanResultInputForEval[key] = testFailureMessages[key];
    });
  }
  return loanResultInputForEval;
}

// Counts failed/waived tests for the loan's assigned partner. Extracted out
// of getTestTabResultData's isDSCR branch, which otherwise nests this
// forEach -> if -> filter chain another two levels deep.
function countAssignedPartnerFailedTests(
  data: ObjectType,
  assignedPartner: string,
  testKey: string[],
): number {
  const currentPartnerData = data?.[assignedPartner];
  const { loanResult } = currentPartnerData;
  const testsData = loanResult?.[Object.keys(loanResult)[0]];
  let failedCountTests = 0;
  Object.keys(testsData)?.forEach((testName: string) => {
    if (testKey.includes(testName)) {
      const failedTests = Object.keys(testsData[testName]).filter(
        (key: any) =>
          ["FAIL", "WAIVED"].includes(
            String(testsData?.[testName]?.[key]).toUpperCase(),
          ) && !EXCLUDED_TEST_NAMES.includes(key),
      );
      failedCountTests += failedTests.length;
    }
  });
  return failedCountTests;
}

// Whether the DSCR template response for this partner still needs to be
// fetched (absent, or present but empty).
function needsDscrTemplateFetch(
  templateResponse: ObjectType,
  key: string,
): boolean {
  return (
    !templateResponse ||
    !templateResponse[key] ||
    Object.keys(templateResponse[key]).length === 0
  );
}

// Runs the full test-result build for one DSCR partner: computes its test
// set, fetches/caches its template response, classifies every test via
// applyDscrTestOutcome, and records the partner's failed tests into
// failTests[idx]. Extracted out of getTestTabResultData's Promise.all(...map)
// so that deeply nested (partner -> per-test) processing lives in its own
// function instead of adding to getTestTabResultData's own nesting depth.
async function processDscrPartnerTests(
  key: string,
  idx: number,
  data: any,
  DSCROrgPartnerMapping: ObjectType,
  templateResponse: ObjectType,
  dynamicConfig: any,
  loanContextData: ObjectType,
  templateLabels: any,
  criticalTestData: ObjectType,
  assignedPartner: string | undefined,
  failTests: any[],
  passTests: any[],
): Promise<void> {
  const tempFailedArr: ObjectType[] = [];
  const programKey = DSCROrgPartnerMapping[key] || `Un-Mapped ORG`;
  const currentPartnerData = data[key];
  let currentPartnerLoanResult =
    currentPartnerData?.[Object.keys(currentPartnerData)[0]];
  currentPartnerLoanResult =
    currentPartnerLoanResult?.[Object.keys(currentPartnerLoanResult)[0]];

  const finalCalculation = currentPartnerLoanResult?.finalCalculation;
  const logicTests = currentPartnerLoanResult?.logicTests;
  const maxMinTests = currentPartnerLoanResult?.maxMinParameters;
  const tests = {
    ...finalCalculation,
    ...logicTests,
    ...maxMinTests,
  };
  const testNamesTemplateBody = Object.keys(tests).map(
    (testName: any) => `testNames.${testName}`,
  );
  const finalCalculationTemplateKeys = Object.keys(
    currentPartnerLoanResult?.finalCalculation || {},
  ).map(
    (testName: any) => `loanResult.finalCalculation.${testName}.message.FAIL`,
  );
  const logicTestsTemplateKeys = Object.keys(
    currentPartnerLoanResult?.logicTests || {},
  ).map((testName: any) => `loanResult.logicTests.${testName}.message.FAIL`);
  const maxMinTestsTemplateKeys = Object.keys(
    currentPartnerLoanResult?.maxMinParameters || {},
  ).map(
    (testName: any) =>
      `loanResult.maxMinParameters.${testName}.message.FAIL`,
  );

  const reqBody = {
    templateKeys: [
      ...testNamesTemplateBody,
      ...finalCalculationTemplateKeys,
      ...logicTestsTemplateKeys,
      ...maxMinTestsTemplateKeys,
    ],
  };
  const partyId = getCookie("partyId") || dynamicConfig?.partyId;
  const orgId =
    loanContextData?.fetchData?.orgId ||
    loanContextData?.loanDetails?.orgId ||
    dynamicConfig?.clientId;

  const tpId = `${key}`;
  // const templateUrl = `public-api/template/fetch?locale=NA&countryCode=NA&partialProcess=true&tpId=${key}`;
  // const failedURL = getURL(templateUrl);

  if (needsDscrTemplateFetch(templateResponse, key)) {
    const templateResponseDSCR = await getTemplateResponseDSCR(
      tpId,
      orgId,
      reqBody,
    );
    templateResponse[key] = templateResponseDSCR;
  }
  const loanResultInputForEval: any = buildTestFailureMessagesMap(
    currentPartnerLoanResult?.testFailureMessages,
  );

  Object.keys(tests)?.forEach((testName: string) => {
    applyDscrTestOutcome(
      testName,
      tests,
      logicTests,
      maxMinTests,
      templateResponse,
      key,
      currentPartnerLoanResult,
      templateLabels,
      criticalTestData,
      loanResultInputForEval,
      tempFailedArr,
      passTests,
      assignedPartner,
    );
  });

  // if (tempFailedArr.length)
  failTests[idx] = { [programKey]: tempFailedArr };
  // failTests.push({ [programKey]: tempFailedArr });
}

// Keeps only the dscrPartner entries that are actually present in data,
// keyed by partner id. Extracted out of getTestTabResultData's isDSCR
// branch, which otherwise nests this for -> if another two levels deep.
function filterDscrPartnerData(dscrPartner: any[], data: any): ObjectType {
  const testData: ObjectType = {};
  for (let idx = 0; idx <= dscrPartner?.length - 1; idx++) {
    const currentData: any = dscrPartner[idx];
    if (data[currentData.id]) {
      testData[currentData.id] = data[currentData.id];
    }
  }
  return testData;
}

// Runs the full non-DSCR (guideline) test-result build: resolves the
// loanResult/loanKey (returning null when either is missing, matching the
// original early `return;`s in getTestTabResultData), fetches/caches the
// template response, and classifies every test via
// applyGuidelineTestOutcome. Returns the (possibly refreshed)
// templateResponse and the resulting failedCountTests so the caller can
// assign them back onto its own local variables.
async function processGuidelineTests(
  data: any,
  publicAPI2Headers: ObjectType,
  dynamicConfig: any,
  templateResponse: ObjectType,
  loanDetails: ObjectType,
  fetchData: ObjectType,
  templateLabels: any,
  failTests: any[],
  passTests: any[],
): Promise<{ templateResponse: ObjectType; failedCountTests: number } | null> {
  // const loanKey = Object.keys(data?.loanResult)[0];
  // const guidelineTests = data?.loanResult?.[loanKey]?.guidelineTest;
  const loanResult = data?.loanResult;

  let guidelineTests = null;

  if (!loanResult) {
    return null;
  }
  const [loanKey] = Object.keys(loanResult);
  if (!loanKey) {
    return null;
  }
  guidelineTests = loanKey ? loanResult[loanKey]?.guidelineTest : undefined;
  const loanEconomicsTests = data?.loanResult?.[loanKey]?.loanEconomics ?? {};
  const tests = { ...guidelineTests, ...loanEconomicsTests };
  const testNamesTemplateBody = Object.keys(tests).map(
    (testName: any) => `testNames.${testName}`,
  );
  const guidelineTestTemplateKeys = Object.keys(
    data?.loanResult?.[loanKey]?.guidelineTest,
  ).map(
    (testName: any) => `loanResult.guidelineTest.${testName}.message.FAIL`,
  );

  const loanEconomicsTestTemplateKeys = Object.keys(
    data?.loanResult?.[loanKey]?.loanEconomics ?? {},
  ).map(
    (testName: any) => `loanResult.loanEconomics.${testName}.message.FAIL`,
  );

  const reqBody = {
    templateKeys: [
      ...testNamesTemplateBody,
      ...guidelineTestTemplateKeys,
      ...loanEconomicsTestTemplateKeys,
    ],
  };
  const partyId = getCookie("partyId") || dynamicConfig?.partyId;

  let nextTemplateResponse = templateResponse;
  if (
    !nextTemplateResponse ||
    Object.keys(nextTemplateResponse).length === 0
  ) {
    nextTemplateResponse = await getTemplateResponse(
      partyId,
      reqBody,
      publicAPI2Headers,
    );
  }

  let failedCountTests = 0;
  Object.keys(tests)?.forEach((testName: string) => {
    failedCountTests += applyGuidelineTestOutcome(
      testName,
      tests,
      guidelineTests,
      loanEconomicsTests,
      nextTemplateResponse,
      data,
      loanKey,
      loanDetails,
      fetchData,
      templateLabels,
      failTests,
      passTests,
    );
  });

  return { templateResponse: nextTemplateResponse, failedCountTests };
}

export const getTestTabResultData = async (
  data: any,
  tempLoanData: any,
  publicAPI2Headers: ObjectType,
  isDSCR: boolean,
  tempLoanResult: ObjectType,
  loanTypeStr: string,
  fetchData: ObjectType,
  loanContextData: ObjectType,
  RootContextData: ObjectType,
  templateResponseData: ObjectType,
  setTemplateResponseData: any,
  dynamicConfig: any,
  templateLabels: any,
  assignedPartner?: string,
  tempCriticalTests?: any,
) => {
  const criticalTestData = { ...(tempCriticalTests ?? {}) };
  const { DSCROrgPartnerMapping, dscrPartner, originatorAccountMappingData } =
    RootContextData;
  const {
    setLeverageAndPricingData,
    setTestTabData,
    setFailedFinalRate,
    loanDetails,
    setAssignedPartner
  } = loanContextData;
  let templateResponse = templateResponseData || {};

  try {
    const failTests: any = [];
    const passTests: any = [];
    let failedCountTests: number = 0;

    if (isDSCR) {
      setAssignedPartner(assignedPartner ?? (loanDetails?.takeOutPartner || ""));
      const testData: any = filterDscrPartnerData(dscrPartner, data);

      const testKey = ["logicTests", "finalCalculation", "maxMinParameters"];
      await Promise.all(
        Object.keys(testData)?.map((key: string, idx: number) =>
          processDscrPartnerTests(
            key,
            idx,
            data,
            DSCROrgPartnerMapping,
            templateResponse,
            dynamicConfig,
            loanContextData,
            templateLabels,
            criticalTestData,
            assignedPartner,
            failTests,
            passTests,
          ),
        ),
      );
      failedCountTests = assignedPartner
        ? countAssignedPartnerFailedTests(data, assignedPartner, testKey)
        : 0;
    } else {
      const guidelineOutcome = await processGuidelineTests(
        data,
        publicAPI2Headers,
        dynamicConfig,
        templateResponse,
        loanDetails,
        fetchData,
        templateLabels,
        failTests,
        passTests,
      );
      if (!guidelineOutcome) {
        return;
      }
      templateResponse = guidelineOutcome.templateResponse;
      failedCountTests = guidelineOutcome.failedCountTests;
    }

    const getResult: any = normalizeEvaluationResult(
      tempLoanResult,
      loanTypeStr,
      { ...tempLoanData, originatorAccountMappingData },
      fetchData,
      failedCountTests ?? null,
    );

    if (
      !templateResponseData ||
      Object.keys(templateResponseData).length === 0
    ) {
      setTemplateResponseData(templateResponse);
    }
    setLeverageAndPricingData(getResult || {});
    setTestTabData(failTests);
    setFailedFinalRate(
      !(
        getResult?.leverageCards?.finalRate ||
        getResult?.leverageCards?.finalPrice
      ),
    );
    setFailedFinalRate(
      !(
        getResult?.leverageCards?.finalRate ||
        getResult?.leverageCards?.finalPrice
      ),
    );
    return failTests;
  } catch (err) {
    console.error(err);
  }
};

const postalOnChange = async (
  event: React.ChangeEvent<HTMLInputElement>,
  newValue: ZipCodeSuggestion | null,
  childIdx: number | undefined,
  field: ObjectType,
  translation: any,
  loanContextData: ObjectType,
) => {
  const {
    addressInputValue,
    propFormErrors,
    propertyDetails,
    setPropertyDetails,
    loanFormData,
    setLoanFormData,
    zipCodeOptions,
    setZipCodeOptions,
    setPropFormErrors,
    setDebouncedValue,
  } = loanContextData;
  setZipCodeOptions(newValue ? [newValue, ...zipCodeOptions] : zipCodeOptions);
  const tempPropFormErrors = [...propFormErrors];
  if (newValue) {
    const { city, state_abbreviation, zipCode } = newValue;
    const tempFormData = propertyDetails?.map(
      (propDetail: ObjectType, idx: number) => {
        if (idx === childIdx) {
          tempPropFormErrors[idx] = {
            ...tempPropFormErrors[idx],
            ["propertyLocation"]: {
              ...tempPropFormErrors[idx]?.["propertyLocation"],
              ["address"]:
                translation(
                  field?.getFieldErrors(addressInputValue[idx ?? 0]),
                ) ?? "",
              ["city"]: translation(field?.getFieldErrors(city)) ?? "",
              ["state"]:
                translation(field?.getFieldErrors(state_abbreviation)) ?? "",
              ["postalCode"]: translation(field?.getFieldErrors(zipCode)) ?? "",
            },
          };
          const prevCity = propDetail?.propertyLocation?.city;
          const prevState = propDetail?.propertyLocation?.state;

          return {
            ...propDetail,
            propertyLocation: {
              ...propDetail.propertyLocation,
              ["address"]: `${addressInputValue[idx ?? 0]
                ?.replace(prevCity, city)
                ?.replace(prevState, state_abbreviation)}`,
              ["city"]: city,
              ["state"]: state_abbreviation,
              ["postalCode"]: zipCode,
            },
          };
        }
        return propDetail;
      },
    );
    setPropFormErrors(tempPropFormErrors);
    setPropertyDetails(tempFormData);
    setLoanFormData({ ...loanFormData, propertyDetails: tempFormData });
  }
  postGuidelineEvaluation(setDebouncedValue, newValue);
};

const addressOnChange = async (
  event: React.ChangeEvent<HTMLInputElement>,
  newValue: LocationSuggestion | null,
  childIdx: number | undefined,
  field: ObjectType,
  translation: any,
  loanContextData: ObjectType,
) => {
  const {
    propFormErrors,
    propertyDetails,
    setPropertyDetails,
    loanFormData,
    setLoanFormData,
    addressOptions,
    setAddressOptions,
    setPropFormErrors,
    setDebouncedValue,
  } = loanContextData;
  setAddressOptions(newValue ? [newValue, ...addressOptions] : addressOptions);
  const tempPropFormErrors = [...propFormErrors];
  if (newValue) {
    const { street_line, city, state, zipcode } = newValue;
    const tempFormData = propertyDetails?.map(
      (propDetail: ObjectType, idx: number) => {
        if (idx === childIdx) {
          tempPropFormErrors[idx] = {
            ...tempPropFormErrors[idx],
            ["propertyLocation"]: {
              ...tempPropFormErrors[idx]?.["propertyLocation"],
              ["address"]:
                translation(
                  field?.getFieldErrors(`${street_line} ${city} ${state}`),
                ) ?? "",
              ["city"]: translation(field?.getFieldErrors(city)) ?? "",
              ["state"]: translation(field?.getFieldErrors(state)) ?? "",
              ["postalCode"]: translation(field?.getFieldErrors(zipcode)) ?? "",
            },
          };
          return {
            ...propDetail,
            propertyLocation: {
              ...propDetail.propertyLocation,
              ["address"]: `${street_line} ${city} ${state}`,
              ["city"]: city,
              ["state"]: state,
              ["postalCode"]: zipcode,
            },
          };
        }
        return propDetail;
      },
    );

    setPropFormErrors(tempPropFormErrors);
    setPropertyDetails(tempFormData);
    setLoanFormData({ ...loanFormData, propertyDetails: tempFormData });
  }
  postGuidelineEvaluation(setDebouncedValue, newValue);
};

const addressPostalCodeOnInputChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  value: string,
  translation: any,
  loanContextData: ObjectType,
  childIdx: number | undefined,
  field: ObjectType | undefined,
  isAddress?: boolean,
) => {
  const {
    setAddressInputValue,
    setZipCodeInputValue,
    setActivePropertyIndex,
    setBorrowerAddressInputValue
  } = loanContextData;
  if (value.length > 7 && !isAddress) return;
  setActivePropertyIndex(childIdx ?? 0);

  if (isAddress) {
    if (field?.id === "address") {
      const addressInputValue = [...loanContextData.addressInputValue];
      addressInputValue[childIdx ?? 0] = value;
      setAddressInputValue(addressInputValue);
    } else {
      const borrowerAddressInputValue = [...loanContextData.borrowerAddressInputValue];
      borrowerAddressInputValue[childIdx ?? 0] = value;
      setBorrowerAddressInputValue(borrowerAddressInputValue);
    }
  } else {
    const zipCodeInputValue = [...loanContextData.zipCodeInputValue];
    zipCodeInputValue[childIdx ?? 0] = value;
    setZipCodeInputValue(zipCodeInputValue);
  }
};

const getAddressOptionLabel = (option: LocationSuggestion) => {
  let value = "";
  if (typeof option === "string") {
    value = option;
  } else if (option.street_line) {
    value = `${option.street_line} ${option.city} ${option.state} ${option.zipcode}`;
  } else {
    value = "";
  }
  return value;
};

const getPostalOptionLabel = (option: any) => {
  let value;
  if (typeof option === "string") {
    value = option;
  } else if (option.city) {
    value = `${option.city} ${option.state_abbreviation} ${option.zipCode}`;
  } else {
    value = "";
  }
  return value;
};

// value === "Yes" ⇒ visible, matching the original if/else pairs in
// applyCondoFeeVisibility below (extracted so isVisible is derived once,
// per feeEle, instead of via a repeated if/else block for each field).
function toYesVisibleFlag(value: any): boolean {
  return value === "Yes";
}

function applyCondoFeeVisibility(feeEle: any, field: any, value: any): any {
  if (
    feeEle.label === "ownerControlStartDate" &&
    field.label === "hoaUnderOwnerControl"
  ) {
    feeEle.isVisible = toYesVisibleFlag(value);
  }
  if (
    feeEle.label === "indivdualOwnedUnits" &&
    field.label === "individualOwnershipAboveTwentyFive"
  ) {
    feeEle.isVisible = toYesVisibleFlag(value);
  }
  if (
    feeEle.label === "hoawithLitOrBankruptcyDesc" &&
    field.label === "hoawithLitOrBankruptcy"
  ) {
    feeEle.isVisible = toYesVisibleFlag(value);
  }
  if (
    feeEle.label === "restrictOwnerToRent" &&
    field.label === "restrictOwnerToRentOutDesc"
  ) {
    feeEle.isVisible = toYesVisibleFlag(value);
  }
  return { ...feeEle };
}

export function handleCondoEligibilityModalChange(
  value: any,
  field: any,
  subComponentLabel: string,
  translation: any,
  loanContextData: ObjectType,
) {
  const {
    condoEligibilityConfig,
    condoEligibilityFormData,
    setCondoEligibilityConfig,
    setCondoEligibilityFormData,
  } = loanContextData;
  let tempConfig = { ...condoEligibilityConfig };

  tempConfig = {
    ...tempConfig,
    fields: {
      ...tempConfig?.fields,
      fee: tempConfig?.fields?.fee?.map((feeEle: any) =>
        applyCondoFeeVisibility(feeEle, field, value),
      ),
    },
  };

  setCondoEligibilityConfig(tempConfig);
  setCondoEligibilityFormData({
    ...condoEligibilityFormData,
    [field.label]: value,
  });
}

function getTitleInfoClassName(
  field: ObjectType,
  isAutoComplete: boolean,
): string {
  if (isAutoComplete || field.type() === "checkBox") {
    return "div-space-between mb-0";
  }
  return "div-space-between mb-14";
}

function getAuditInfo(
  field: ObjectType,
  auditContext: { section?: string; propertyId?: number; unitId?: number } | undefined,
  auditedFieldPaths: string[] | undefined,
): { auditPath: string | null; isAudited: boolean } {
  const { section, propertyId, unitId } = auditContext ?? {};
  if (!section) return { auditPath: null, isAudited: false };
  const auditPath = getAuditFieldPath(section, field.id ?? field.label, propertyId, unitId);
  return { auditPath, isAudited: !!auditedFieldPaths?.includes(auditPath) };
}

function getFieldLabelAndTitle(
  field: ObjectType,
  translation: any,
): { label: string; title: string } {
  const medianSuffix = field?.isMedian ? ` (${translation("median")})` : "";
  const ifApplicableSuffix = field?.ifApplicable
    ? ` ${translation("ifApplicable")}`
    : "";
  const label = `${translation(
    field?.displayLabel || field?.fieldLabel || field?.label,
  )}
    ${medianSuffix}
    ${ifApplicableSuffix}`;
  const title = translation(field?.title) || label;
  return { label, title };
}

function getRequiredMarkerVisibility(
  field: ObjectType,
  loanType: string | undefined,
  defaultSelectedDataView: any,
): { showInline: boolean; showBelow: boolean } {
  const isFieldRequired =
    field.required && !field?.isAutoCalculated(loanType, defaultSelectedDataView);
  const isInPlaceLeaseRent = field.id === "inPlaceLeaseRentMonthly";
  return {
    showInline: isFieldRequired && !isInPlaceLeaseRent,
    showBelow: isFieldRequired && isInPlaceLeaseRent,
  };
}

export function getTitleInfo(
  field: ObjectType,
  isAutoComplete: boolean,
  loanType: string | undefined,
  translation: any,
  loanContextData: ObjectType,
  auditContext?: { section?: string; propertyId?: number; unitId?: number },
) {
  const {
    expandLeverageAndTests,
    defaultSelectedDataView,
    setCondoEligibility,
    auditedFieldPaths,
  } = loanContextData;

  const { auditPath, isAudited } = getAuditInfo(
    field,
    auditContext,
    auditedFieldPaths,
  );
  const className = getTitleInfoClassName(field, isAutoComplete);
  const { label, title } = getFieldLabelAndTitle(field, translation);
  const { showInline: showInlineRequiredMarker, showBelow: showBelowRequiredMarker } =
    getRequiredMarkerVisibility(field, loanType, defaultSelectedDataView);
  const showAutoCalculatedChip =
    field?.isAutoCalculated?.(loanType, defaultSelectedDataView) &&
    !expandLeverageAndTests;

  return (
    <div className={className}>
      <div
        className={`d-flex w-80`}
        style={{
          marginTop: expandLeverageAndTests ? "15px" : "",
          alignItems: "center",
        }}
      >
        <div className="question-labels truncate-words" title={title}>
          {label}
        </div>
        <div style={{ maxHeight: "20px" }}>
          {showInlineRequiredMarker ? (
            <sup className="label-required">*</sup>
          ) : null}
        </div>
        {field?.info ? (
          <Tooltip title={translation(field.info)}>
            <InfoOutlinedIcon fontSize="small" style={{ height: "14px" }} />
          </Tooltip>
        ) : null}
        {isAudited && auditPath ? <AuditTag field={auditPath} /> : null}
      </div>
      {showBelowRequiredMarker ? (
        <sup className="label-required">*</sup>
      ) : null}
      {field.checkEligibility ? (
        <div
          className="div-start align-center cursor-ptr"
          onClick={() => setCondoEligibility(true)}
        >
          <a>{translation(field.label)}</a>
          &ensp;
          <img src={ShareIcon} alt="share-icon.svg" />
        </div>
      ) : null}
      {showAutoCalculatedChip ? (
        <div>
          <CustomChip
            size="small"
            label={translation("autoCalculated")}
            variant="outlined"
            className="chipData"
          />
        </div>
      ) : null}
    </div>
  );
}

const updateVisibility = (
  field: ObjectType,
  value: any,
  tempPropertyDetailsConfig: any,
  propertyDetails: any,
  chipsData: string[],
  childIdx?: number,
) => {
  return tempPropertyDetailsConfig?.map((propField: any, propIdx: number) => {
    if (propIdx === childIdx) {
      return {
        ...propField,
        fields: propField?.fields?.map((subcomponent: ObjectType) => {
          if (subcomponent.label === "propertyInformation") {
            return {
              ...subcomponent,
              fields: subcomponent.fields.map((indField: ObjectType) => {
                if (
                  field.label === "propertyType" &&
                  indField.label === "numberOfUnits"
                ) {
                  if (["Condo", "SFR"].includes(value)) {
                    return {
                      ...indField,
                      isVisible: (value = false),
                    };
                  } else {
                    return {
                      ...indField,
                      isVisible: (value = true),
                    };
                  }
                } else if (
                  ["numberOfUnits", "propertyType"].includes(field.label) &&
                  ["numberOfUnits", "propertyType"].includes(indField.label) &&
                  propertyDetails[childIdx]?.["propertyInformation"]?.[
                    "propertyType"
                  ] === "2-4 Unit"
                ) {
                  indField.isPopUpMessage = +value <= 1;
                } else {
                  indField.isPopUpMessage = false;
                }
                return indField;
              }),
            };
          }
          if(subcomponent.label === "propertyEconomics") {
            return {
              ...subcomponent,
              fields: subcomponent.fields.map((indField: ObjectType) => {

                // if (
                //   field.label === "originalDateOfAcquisition" &&
                //   indField.label === "verifiedCostsAndImprovements"
                // ) {
                //   if (isWithinLast6Months(value)) {
                //     return {
                //       ...indField,
                //       required: true,
                //     };
                //   } else {
                //     return {
                //       ...indField,
                //       required: false,
                //     };
                //   }
                // }
                
                return indField;
              }),
            };
          }
          return subcomponent;
        }),
      };
    }
    return propField;
  });
};

// Recomputes a single unit-level field's visibility when
// currentLeaseTermsInMonths changes. Extracted out of the deeply nested
// propConfig -> propField -> unitInfo -> unitField map chain in
// getDynamicFields so that chain doesn't also have to nest this field's
// own dynamicFields.forEach check.
function computeLeaseTermUnitField(
  unitField: ObjectType,
  value: any,
): ObjectType {
  const tempUnitField = { ...unitField };
  const isZero = +value === 0;
  const isInRange1to5 = +value >= 1 && +value <= 5;
  const dynamicFields = [
    "currentUnitUsage",
    "rentalCharacterization",
    "monthlyMarketRent",
    "monthlyUnitRent",
    "tenantOccupancy",
    "operatingHistory",
  ];
  dynamicFields.forEach((fieldStr) => {
    if (fieldStr === tempUnitField.label) {
      if (
        (isZero &&
          [
            "currentUnitUsage",
            "rentalCharacterization",
            "monthlyMarketRent",
            "monthlyUnitRent",
          ].includes(fieldStr)) ||
        (isInRange1to5 &&
          [
            "rentalCharacterization",
            "monthlyMarketRent",
            "monthlyUnitRent",
            "tenantOccupancy",
            "operatingHistory",
          ].includes(fieldStr)) ||
        (+value > 5 &&
          [
            "rentalCharacterization",
            "monthlyMarketRent",
            "monthlyUnitRent",
          ].includes(fieldStr))
      ) {
        tempUnitField.minimumDataView = true;
      } else {
        tempUnitField.minimumDataView = false;
      }
      tempUnitField.isVisible = tempUnitField.minimumDataView;
    }
  });
  return tempUnitField;
}

// Applies the currentLeaseTermsInMonths recalculation to the one unitInfo
// entry that matches unitIdx/subComponentLabel, leaving every other
// unitInfo entry untouched (shallow-copied, as the original code did).
function applyCurrentLeaseTermsToUnit(
  unitInfo: ObjectType,
  unitInfoIdx: number,
  unitIdx: number | undefined,
  subComponentLabel: string,
  field: any,
  value: any,
): ObjectType {
  if (unitInfoIdx === unitIdx && unitInfo.label === subComponentLabel) {
    const unitInfoArr = unitInfo.fields.map((unitField: ObjectType) => {
      if (field.label === "currentLeaseTermsInMonths") {
        return computeLeaseTermUnitField(unitField, value);
      }
      return unitField;
    });
    return {
      ...unitInfo,
      fields: unitInfoArr,
    };
  }
  return { ...unitInfo };
}

function getDynamicFields(
  value: any,
  field: any,
  subComponentLabel: string,
  loanContextData: ObjectType,
  component?: ObjectType | undefined,
  childIdx?: number | undefined,
  unitIdx?: number,
  chipsData?: any,
  tempValue?: any,
) {
  const {
    loanDetailsConfig,
    propertyDetailsConfig,
    borrowerInformationConfig,
    autoCalculatedLoanResults,
    loanDetails,
    propertyDetails,
  } = loanContextData;

  let tempLoanDetailsConfig = { ...loanDetailsConfig };
  let tempPropertyDetailsConfig = [...propertyDetailsConfig];
  let tempBorrowerInformationConfig = { ...borrowerInformationConfig };

  if (["loanDetails", "borrowerDetails"].includes(component?.label)) {
    const dynamicLoanDetailsFields = [
      "borrowerGuarantor",
      "loanPurpose",
      "interestOnlyFlag",
      "immigrationStatus",
      "pledgeOfEquity",
      "crossCollaterlization",
      "predominantState",
      "numberOfProperties",
      "includeOutOfPocketBudgetARLTV",
      "totalRehabBudget",
      "totalBudget",
      "financedBudget",
      "numberOfUnits",
      "propertyType",
      "firstTimeHomeBuyer",
      "borrowerType",
      "rateType",
    ];
    if (dynamicLoanDetailsFields.includes(field.label)) {
      tempBorrowerInformationConfig = {
        ...tempBorrowerInformationConfig,
        fields: tempBorrowerInformationConfig?.fields.map(
          (subComponent: ObjectType) => {
            const subComponentFields = subComponent.fields.map(
              (fieldObj: ObjectType) => {
                if (subComponent.label !== subComponentLabel) {
                  return fieldObj;
                }
                if (
                  fieldObj.label === "immigrationStatus" &&
                  field.label === "borrowerGuarantor"
                ) {
                  fieldObj.minimumDataView = value === "Yes";
                  fieldObj.isVisible = value === "Yes";
                }
                if (
                  fieldObj.label === "firstTimeHomeBuyer" &&
                  field.label === "borrowerType"
                ) {
                  fieldObj.minimumDataView = value === "Individual";
                  fieldObj.isVisible = value === "Individual";
                }
                if (
                  ["creditScoreActual", "originalCreditScore"].includes(
                    fieldObj.label,
                  ) &&
                  field.label === "immigrationStatus"
                ) {
                  fieldObj.required = value !== "Foriegn National";
                }
                if (
                  ["creditScoreActual", "originalCreditScore"].includes(
                    fieldObj.label,
                  ) &&
                  field.label === "borrowerGuarantor"
                ) {
                  const immigrationStatusObj = subComponent.fields.find(
                    (ele: ObjectType) => ele.label === "immigrationStatus",
                  );
                  fieldObj.required = immigrationStatusObj?.isVisible
                    ? value !== "No"
                    : true;
                }
                return fieldObj;
              },
            );
            return {
              ...subComponent,
              fields: subComponentFields,
            };
          },
        ),
      };

      tempLoanDetailsConfig = {
        ...tempLoanDetailsConfig,
        fields: tempLoanDetailsConfig?.fields.map(
          (subComponent: ObjectType) => {
            const subComponentFields = subComponent.fields.map(
              (fieldObj: ObjectType) => {
                if (subComponent.label === subComponentLabel) {
                  if (
                    fieldObj.label === "includeOutOfPocketBudgetARLTV" &&
                    [
                      "totalRehabBudget",
                      "financedBudget",
                      "totalBudget",
                    ].includes(field.label)
                  ) {
                    const value1 = ["totalRehabBudget", "totalBudget"].includes(
                      field.label,
                    )
                      ? +value
                      : chipsData.includes("Ground Up")
                        ? +autoCalculatedLoanResults?.loanEconomics?.totalBudget
                        : +loanDetails?.loanEconomics?.totalRehabBudget || "";
                    const value2 =
                      field.label === "financedBudget"
                        ? +value
                        : +loanDetails?.loanEconomics?.financedBudget || "";
                    fieldObj.minimumDataView = value1 !== value2;
                    fieldObj.isVisible = value1 !== value2;
                  }
                  if (
                    fieldObj.label === "crossCollaterlization" &&
                    field.label === "numberOfProperties"
                  ) {
                    fieldObj.minimumDataView = value > 1;
                    fieldObj.isVisible = value > 1;
                  }
                  if (
                    fieldObj.label === "immigrationStatus" &&
                    field.label === "borrowerGuarantor"
                  ) {
                    fieldObj.minimumDataView = value === "Yes";
                    fieldObj.isVisible = value === "Yes";
                  }
                  if (
                    fieldObj.label === "firstTimeHomeBuyer" &&
                    field.label === "borrowerType"
                  ) {
                    fieldObj.minimumDataView = value === "Individual";
                    fieldObj.isVisible = value === "Individual";
                  }
                  if (
                    fieldObj.label === "ioPeriod" &&
                    field.label === "interestOnlyFlag"
                  ) {
                    fieldObj.minimumDataView = value === "Yes";
                    fieldObj.isVisible = value === "Yes";
                  }
                  if (
                    ["predominantState", "predominantPropertyType"].includes(
                      fieldObj.label,
                    ) &&
                    field.label === "crossCollaterlization"
                  ) {
                    fieldObj.minimumDataView = [value, tempValue].includes("Y");
                    fieldObj.isVisible = [value, tempValue].includes("Y");
                  }
                  if (
                    ["predominantState", "predominantPropertyType"].includes(
                      fieldObj.label,
                    ) &&
                    field.label === "numberOfProperties"
                  ) {
                    fieldObj.minimumDataView = value > 1;
                    fieldObj.isVisible = value > 1;
                  }
                  if (
                    ["creditScoreActual", "originalCreditScore"].includes(
                      fieldObj.label,
                    ) &&
                    field.label === "immigrationStatus"
                  ) {
                    fieldObj.required = value !== "Foriegn National";
                  }
                  if (
                    ["creditScoreActual", "originalCreditScore"].includes(
                      fieldObj.label,
                    ) &&
                    field.label === "borrowerGuarantor"
                  ) {
                    const immigrationStatusObj = subComponent.fields.find(
                      (ele: ObjectType) => ele.label === "immigrationStatus",
                    );
                    fieldObj.required = immigrationStatusObj?.isVisible
                      ? value !== "No"
                      : true;
                  }
                  if (
                    ["totalRehabBudget", "totalBudget"].includes(
                      fieldObj.label,
                    ) &&
                    ["totalRehabBudget", "totalBudget"].includes(field.label)
                  ) {
                    if (
                      chipsData.includes("Stabilized") &&
                      !chipsData.includes("Multifamily/Mixed Use") &&
                      +value > 0
                    ) {
                      fieldObj.isPopUpMessage = true;
                    } else {
                      delete fieldObj.isPopUpMessage;
                    }
                  }
                  if (
                    fieldObj.label === "financedBudget" &&
                    [
                      "totalRehabBudget",
                      "financedBudget",
                      "totalBudget",
                    ].includes(field.label)
                  ) {
                    const value1 = ["totalRehabBudget", "totalBudget"].includes(
                      field.label,
                    )
                      ? +value
                      : chipsData.includes("Ground Up")
                        ? +autoCalculatedLoanResults?.loanEconomics?.totalBudget
                        : ((+autoCalculatedLoanResults?.loanEconomics
                            ?.totalRehabBudget ||
                            +loanDetails?.loanEconomics?.totalRehabBudget) ??
                          "");
                    const value2 =
                      field.label === "financedBudget"
                        ? +value
                        : +(loanDetails?.loanEconomics?.financedBudget ?? 0);
                    fieldObj.isPopUpMessage = value1 !== value2;
                  }
                  if (
                    field.label === "rateType" &&
                    dscrArmFields.includes(fieldObj.label)
                  ) {
                    const isAdjustable = value === "Adjustable";
                    fieldObj.isVisible = isAdjustable;
                    fieldObj.isRequired = isAdjustable;
                  }
                  if(fieldObj.label === "cashOutRefinance" && field.label === "loanPurpose" ){
                    const loanPurpose = value;
                    fieldObj.isVisible = loanPurpose !== "Purchase";
                    fieldObj.isRequired = loanPurpose !== "Purchase";
                  }
                  return fieldObj;
                }

                if (
                  fieldObj.label === "requestedLTPP" &&
                  field.label === "loanPurpose"
                ) {
                  fieldObj.minimumDataView = value === "Purchase";
                  fieldObj.isVisible = value === "Purchase";
                }
                return fieldObj;
              },
            );
            return {
              ...subComponent,
              fields: subComponentFields,
            };
          },
        ),
      };

      if (["loanPurpose"].includes(field.label)) {
        tempPropertyDetailsConfig = tempPropertyDetailsConfig?.map(
          (propField) => {
            return {
              ...propField,
              fields: propField?.fields?.map((subcomponent: ObjectType) => {
                if (subcomponent.label === "propertyInformation") {
                  return {
                    ...subcomponent,
                    fields: subcomponent.fields.map((indField: ObjectType) => {
                      if (
                        indField.label === "isPurchasedWithDebt" &&
                        field.label === "loanPurpose"
                      ) {
                        return {
                          ...indField,
                          isVisible: value === "Delayed Purchase",
                        };
                      } else {
                        return indField;
                      }
                    }),
                  };
                } else {
                  return subcomponent;
                }
              }),
            };
          },
        );
      }
    } else if (field.label === "exitStrategy") {
      tempPropertyDetailsConfig = tempPropertyDetailsConfig?.map(
        (propField) => {
          return {
            ...propField,
            fields: propField?.fields?.map((subcomponent: ObjectType) => {
              if (subcomponent.label === "propertyEconomics") {
                return {
                  ...subcomponent,
                  fields: subcomponent.fields.map((indField: ObjectType) => {
                    if (
                      indField.label === "estimatedRentPerYearUponCompletion" &&
                      field.label === "exitStrategy"
                    ) {
                      return {
                        ...indField,
                        minimumDataView: value === "Rehab to Hold/Rent",
                        isVisible: [
                          "Rehab to Hold/Rent",
                          "Build to Flip/Sell",
                          "Build to Rent",
                        ].includes(value),
                      };
                    }
                    if (
                      indField.label === "grossPotentialRentMonthly" &&
                      field.label === "exitStrategy"
                    ) {
                      return {
                        ...indField,
                        minimumDataView: value === "Build to Rent",
                        isVisible: value === "Build to Rent",
                      };
                    } else {
                      return indField;
                    }
                  }),
                };
              } else {
                return subcomponent;
              }
            }),
          };
        },
      );
    }
  } else {
    if (field.label === "currentLeaseTermsInMonths") {
      tempPropertyDetailsConfig = tempPropertyDetailsConfig?.map(
        (propConfig: ObjectType, propIdx: number) => {
          if (propIdx === childIdx) {
            const tempPropConfig = propConfig?.fields.map(
              (propField: any, propFieldIdx: number) => {
                if (Array.isArray(propField)) {
                  const tempPropField = propField.map(
                    (unitInfo: ObjectType, unitInfoIdx: number) =>
                      applyCurrentLeaseTermsToUnit(
                        unitInfo,
                        unitInfoIdx,
                        unitIdx,
                        subComponentLabel,
                        field,
                        value,
                      ),
                  );
                  return tempPropField;
                }
                return propField;
              },
            );
            return { ...propConfig, fields: tempPropConfig };
          }
          return propConfig;
        },
      );
    }
    if (["propertyType", "numberOfUnits"].includes(field.label)) {
      tempPropertyDetailsConfig = updateVisibility(
        field,
        value,
        tempPropertyDetailsConfig,
        propertyDetails,
        chipsData,
        childIdx,
      );
    }
  }
  return {
    tempLoanDetailsConfig,
    tempPropertyDetailsConfig,
    tempBorrowerInformationConfig,
  };
}

function updateChipsData(
  fetchData: any,
  chipsData: any,
  setChipsData: any,
  newPropertyType?: string,
) {
  const tempFetchData = { ...fetchData };
  const indexOfMostRestrictiveProperty =
    tempFetchData?.autoCalculatedLoanResults?.loanCharacterisation
      ?.indexOfMostRestrictiveProperty ?? 0;
  const propertyType =
    tempFetchData?.propertyDetails?.[indexOfMostRestrictiveProperty]
      ?.propertyInformation?.propertyType;
  const newChipsData: string[] = chipsData.map((chip: string) => {
    if (chip === propertyType) {
      // if (chip === tempFetchData?.propertyType) {
      if (newPropertyType === "SFR") {
        return "Single Family/Detached";
      }
      return newPropertyType;
    }
    return chip;
  });
  setChipsData([...new Set(newChipsData ?? [])]);
  fetchData.propertyType =
    newPropertyType === "SFR" ? "Single Family/Detached" : newPropertyType;
}

// Shared "is this required field currently invalid" check used by
// checkMinDataFilled below. Extracted so the nested field-tree traversal
// doesn't have to inline the check at every nesting level.
function isRequiredFieldInError(
  field: ObjectType,
  loanType: string | undefined,
  defaultSelectedDataView: string,
  value: any,
): boolean {
  if (
    field.required &&
    (defaultSelectedDataView === "minimumDataView"
      ? field?.minimumDataView
      : field?.isVisible) &&
    !field?.isAutoCalculated(loanType, defaultSelectedDataView)
  ) {
    const error = field.getFieldErrors(value);
    if (error) {
      return true;
    }
  }
  return false;
}

function hasLoanDetailsError(
  loanDetailsConfig: ObjectType,
  loanDetails: ObjectType,
  loanType: string | undefined,
  defaultSelectedDataView: string,
): boolean {
  let isError = false;
  loanDetailsConfig?.fields?.map((subField: ObjectType) => {
    subField?.fields?.map((field: ObjectType) => {
      if (
        isRequiredFieldInError(
          field,
          loanType,
          defaultSelectedDataView,
          loanDetails?.[subField?.label]?.[field.label],
        )
      ) {
        isError = true;
      }
    });
  });
  return isError;
}

function hasUnitFieldsError(
  propSubField: ObjectType,
  propertyDetails: ObjectType,
  parentIdx: number,
  loanType: string | undefined,
  defaultSelectedDataView: string,
): boolean {
  let isError = false;
  propSubField?.map(
    (unitParentField: ObjectType, unitParentIdx: number) => {
      unitParentField?.fields?.map((unitField: ObjectType) => {
        if (
          isRequiredFieldInError(
            unitField,
            loanType,
            defaultSelectedDataView,
            propertyDetails[parentIdx]?.[unitParentField.label][
              unitParentIdx
            ]?.[unitField.label],
          )
        ) {
          isError = true;
        }
      });
    },
  );
  return isError;
}

function hasPropertyDetailsError(
  propertyDetailsConfig: ObjectType,
  propertyDetails: ObjectType,
  loanType: string | undefined,
  defaultSelectedDataView: string,
): boolean {
  let isError = false;
  propertyDetailsConfig?.map((propConfig: ObjectType, parentIdx: number) => {
    propConfig?.fields?.map((propSubField: ObjectType) => {
      if (!Array.isArray(propSubField)) {
        propSubField?.fields?.map((field: ObjectType) => {
          if (
            isRequiredFieldInError(
              field,
              loanType,
              defaultSelectedDataView,
              propertyDetails[parentIdx]?.[propSubField.label]?.[field.label],
            )
          ) {
            isError = true;
          }
        });
      } else if (
        hasUnitFieldsError(
          propSubField,
          propertyDetails,
          parentIdx,
          loanType,
          defaultSelectedDataView,
        )
      ) {
        isError = true;
      }
    });
  });
  return isError;
}

export function checkMinDataFilled(
  loanContextData: ObjectType,
  noAssignedPartnerStr: string,
  loanType?: string,
  fetchData?: any,
) {
  const {
    loanDetailsConfig,
    propertyDetails,
    loanDetails,
    propertyDetailsConfig,
    defaultSelectedDataView,
    setNoAssignedPartner,
  } = loanContextData;
  if (defaultSelectedDataView !== "minimumDataView") {
    setNoAssignedPartner("");
    return;
  }

  const isLoanDetailsError = hasLoanDetailsError(
    loanDetailsConfig,
    loanDetails,
    loanType,
    defaultSelectedDataView,
  );
  const isPropertyDetailsError = hasPropertyDetailsError(
    propertyDetailsConfig,
    propertyDetails,
    loanType,
    defaultSelectedDataView,
  );
  const isError = isLoanDetailsError || isPropertyDetailsError;

  if (!isError || fetchData?.tcLoanId) {
    setNoAssignedPartner(noAssignedPartnerStr);
  } else {
    setNoAssignedPartner("eligibilityAndPricingText");
  }
}

export const fetchGuidelineEvalResult = async (
  loanId: string | any,
  loanType: string | undefined,
  propType: string | undefined,
  loanContextData: ObjectType,
  RootContextData: ObjectType,
  templateResponseData: any,
  setTemplateResponseData: any,
  dynamicConfig: any,
  templateLabels: any,
  tempLPData?: any,
) => {
  const {
    loanDetails,
    propertyDetails,
    setAssignedPartner,
    setLoanDetails,
    autoCalculatedPropResults,
    setRatesAsOfDate,
    defaultSelectedDataView,
    assignedPartner,
    setAutoCalculatedLoanResults,
    setAutoCalculatedPropResults,
    setRulesResponse,
    fetchData,
    chipsData,
    setEligiblePartnerResults,
    setPartnersResults,
    setCriticalTests,
    quickLoanSizingData,
    detailedLoanSizingData,
    selectedSizingType,
    setQuickLoanSizingData,
    setDetailedLoanSizingData,
    detailedLoanSizingDataRef,
    updateDetailedLoanSizingData,
  } = loanContextData;
  try {
    let tempLoanData = Object.keys(loanDetails).length
      ? { ...loanDetails }
      : { ...tempLPData };
    let tempPropertiesResult = [...propertyDetails];
    if (selectedSizingType === SizingType.QUICK_LOAN_SIZING) {
      tempLoanData = {
        borrowerInformation: quickLoanSizingData?.borrowerInformation ?? {},
        loanInformation: quickLoanSizingData?.loanInformation ?? {},
        loanEconomics: quickLoanSizingData?.loanEconomics ?? {},
      };
      tempPropertiesResult = [
        {
          propertyLocation: quickLoanSizingData?.propertyLocation ?? {},
          propertyInformation: quickLoanSizingData?.propertyInformation ?? {},
          propertyEconomics: quickLoanSizingData?.propertyEconomics ?? {},
        },
      ];
    } else if (selectedSizingType === SizingType.DETAILED_LOAN_SIZING) {
      tempLoanData = {
        borrowerInformation: detailedLoanSizingData?.borrowerInformation ?? {},
        loanInformation: detailedLoanSizingData?.loanInformation ?? {},
        loanEconomics: detailedLoanSizingData?.loanEconomics ?? {},
      };
      tempPropertiesResult = detailedLoanSizingData?.propertyDetails;
    }

    let loanTypeParam: string = "";
    if (loanType === "InvestorDSCR") {
      loanTypeParam = "30year";
    } else if (loanType === "BridgeLoan") {
      loanTypeParam = "bridge";
    }

    const loanTypeStr = loanTypeParam === "30year" ? "30Year" : "BRIDGE";
    const orgId =
      loanDetails?.orgId ||
      getCookie("orgId") ||
      fetchData?.orgId ||
      dynamicConfig?.clientId;
    const orgBucketCategory = getCookie("orgBucketCategory");
    const orgFundingType =
      loanDetails?.fundingType || getCookie("orgFundingType");

    const reqBody = normalizeReqBody(
      tempLoanData,
      tempPropertiesResult,
      loanTypeStr,
      loanId,
      defaultSelectedDataView,
      chipsData,
      propType,
      { orgId, orgBucketCategory, orgFundingType },
      fetchData,
      loanDetails,
    );

    const publicAPI2Headers = {
      accept: "application/json",
      "kc-realm": dynamicConfig?.ViteAppKcRealm,
    };

    const { data } = await getGuidlineEvaluationResult(loanType, reqBody);

    let result: ObjectType = data;
    let tempResultKeys: ObjectType = {};
    let tempLoanResult: ObjectType = {};
    let tempPropertyResult: ObjectType = { ...autoCalculatedPropResults };
    // Only reassigned (to the unwrapped first-partner result) in the
    // non-DSCR/guideline branch below when the response turns out to be
    // tpResultMap-wrapped; otherwise stays the raw response, same as before.
    let guidelineResultData: any = data;

    let tempCriticalTests: ObjectType = {};

    for (const tpId of Object.keys(result)) {
      const currentPartner: any = result?.[tpId];
      const ruleVersion: string =
        currentPartner?.loanResult?.[loanId]?.ruleVersion?.RULE_VERSION || "";

      const criticalResponse: any = await getCriticalTests(ruleVersion);
      tempCriticalTests[tpId] = [
        ...(criticalResponse?.data?.criticalTestLoanEligibility ?? []),
      ];
    }
    tempCriticalTests = JSON.parse(JSON.stringify({ ...tempCriticalTests }));
    const tempLoanId: any = loanId;
    if (loanTypeParam === "30year") {
      const eligiblePayload = {
        tpResultMap: {
          [tempLoanId]: {
            FES: data,
          },
        },
        loanId,
        loanStage: "FES",
        loan: {
          loanEconomics: {
            originalLoanAmount: (
              tempLoanData?.loanEconomics?.originalLoanAmount ||
              tempLoanData?.loanEconomics?.initialLoanAmount ||
              tempLoanData?.loanEconomics?.borrowerRequested
            )?.toString(),
          },
          loanDetailId: {
            originatorPartyId: orgId,
          },
        },
      };

      const eligibleResult = await getEligibleResult(
        eligiblePayload,
        publicAPI2Headers,
      );

      let partnerId = "";
      const isTakeoutPartnerOverridden =
        loanDetails?.takeoutPartnerStatus === "OVERRIDDEN" &&
        eligibleResult?.data?.eligiblePartners?.includes(
          loanDetails?.takeOutPartner,
        );
      if (
        eligibleResult?.data?.assignedPartner &&
        eligibleResult?.data?.takeoutPartnerStatus === "PENDING"
      ) {
        partnerId = "";
      } else if (isTakeoutPartnerOverridden) {
        partnerId = loanDetails?.takeOutPartner;
      } else {
        partnerId = eligibleResult?.data?.assignedPartner || "";
      }

      if (partnerId) {
        result = data[partnerId];
        tempResultKeys = Object.keys(result?.loanResult);
        tempLoanResult = result?.loanResult[tempResultKeys[0]];
        tempPropertyResult = result?.propertiesResults;
        setAssignedPartner(partnerId ?? "");
        setLoanDetails({ ...loanDetails, takeOutPartner: partnerId });
        getTestTabResultData(
          data,
          tempLoanData,
          publicAPI2Headers,
          true,
          tempLoanResult,
          loanTypeStr,
          fetchData,
          loanContextData,
          RootContextData,
          templateResponseData,
          setTemplateResponseData,
          dynamicConfig,
          templateLabels,
          partnerId ?? "",
          tempCriticalTests,
        );
        checkMinDataFilled(loanContextData, "", loanType);
      } else {
        checkMinDataFilled(loanContextData, "noAssignedPartner", loanType);
        setAssignedPartner("");
        getTestTabResultData(
          data,
          tempLoanData,
          publicAPI2Headers,
          true,
          tempLoanResult,
          loanTypeStr,
          fetchData,
          loanContextData,
          RootContextData,
          templateResponseData,
          setTemplateResponseData,
          dynamicConfig,
          templateLabels,
          eligibleResult?.data?.assignedPartner ?? "",
          tempCriticalTests,
        );
        const currentKey = Object.keys(data)[0];
        result = data[currentKey];
        tempResultKeys = Object.keys(result?.loanResult);
        tempLoanResult = result?.loanResult[tempResultKeys[0]];
        tempPropertyResult = result?.propertiesResults;
      }
      setEligiblePartnerResults(eligibleResult?.data);
    } else {
      // The guideline-api response for a single-partner evaluation (e.g.
      // bplsizer's Bridge loans) is flat: { loanResult, propertiesResults }.
      // A multi-partner response instead wraps every partner's result under
      // tpResultMap, with no top-level loanResult to read — resolve the
      // first partner's result in that case, mirroring the same
      // shape-detection KDMWrapper's resolveLoanResultData already applies
      // (tpResultMap lookup first, flat loanResult as the fallback), so this
      // branch doesn't silently produce an empty result (and empty
      // leverageCards) when it receives the wrapped shape instead of the
      // flat one it originally assumed.
      guidelineResultData = data?.tpResultMap
        ? data.tpResultMap[Object.keys(data.tpResultMap)[0]]
        : data;
      const loanResult = guidelineResultData?.loanResult;
      tempLoanResult =
        loanResult && Object.keys(loanResult).length
          ? loanResult[Object.keys(loanResult)[0]]
          : null;
      tempPropertyResult = guidelineResultData?.propertiesResults;
    }

    if (loanTypeStr === "BRIDGE") {
      getTestTabResultData(
        guidelineResultData,
        tempLoanData,
        publicAPI2Headers,
        false,
        tempLoanResult,
        loanTypeStr,
        fetchData,
        loanContextData,
        RootContextData,
        templateResponseData,
        setTemplateResponseData,
        dynamicConfig,
        templateLabels,
      );
    }

    // Used to populate the autocalculated fields
    const finalLoanData: any = normalizeResultLoanData(
      tempLoanData,
      tempLoanResult,
      loanTypeStr,
      tempPropertyResult,
      tempPropertiesResult,
      fetchData,
    );

    let asOfDate = tempLoanResult?.ruleVersion?.RATE_SHEET?.substr(1, 8) ?? "";

    if (asOfDate) {
      asOfDate = moment(asOfDate, "DD-MM-YY").format("DD-MM-YYYY");
    }

    const { tempLoanDetails, propertyResult } = finalLoanData;
    setAutoCalculatedLoanResults({ ...tempLoanDetails });
    setAutoCalculatedPropResults([...propertyResult]);
    setRulesResponse(data);
    setRatesAsOfDate(asOfDate);
    setPartnersResults(data);
    setCriticalTests(tempCriticalTests);
    if (selectedSizingType === SizingType.QUICK_LOAN_SIZING) {
      setQuickLoanSizingData({
        ...quickLoanSizingData,
        loanOutputs: tempLoanDetails?.loanEconomics,
      });
    } else if (selectedSizingType === SizingType.DETAILED_LOAN_SIZING) {
      const updatedLoanData = {
        ...detailedLoanSizingData,           // ← current state (has all latest changes)
        loanEconomics: {
          ...detailedLoanSizingData?.loanEconomics,
          totalRehabBudget:
            tempLoanDetails?.loanEconomics?.totalRehabBudget ?? "",
          totalBudget: tempLoanDetails?.loanEconomics?.totalBudget ?? "",
        },
        loanInformation: {
          ...detailedLoanSizingData?.loanInformation,
          requiredInterestReserve:
            tempLoanDetails?.loanInformation?.requiredInterestReserve ?? "",
          requiredInterestReserveMonths:
            tempLoanDetails?.loanInformation?.requiredInterestReserveMonths ?? "",
        },
      };

      // Ref is only used for detecting changes, not as a data source
      if (hasRelevantChanges(detailedLoanSizingDataRef, tempLoanDetails)) {
        updateDetailedLoanSizingData(updatedLoanData);
        // ✅ Sync the ref so hasRelevantChanges returns false on next render
        detailedLoanSizingDataRef.current = updatedLoanData;
      }
    }
    return { rulesData: data, asOfDate, tempCriticalTests };
  } catch (err: any) {
    console.error(err);
  }
};

function hasRelevantChanges(detailedLoanSizingDataRef: any, tempLoanDetails: any) {
  const current = detailedLoanSizingDataRef.current;

  const prevReserve = current?.loanInformation?.requiredInterestReserve;
  const prevReserveMonths = current?.loanInformation?.requiredInterestReserveMonths;
  const prevRehabBudget = current?.loanEconomics?.totalRehabBudget;

  const nextReserve = tempLoanDetails?.loanInformation?.requiredInterestReserve ?? "";
  const nextReserveMonths = tempLoanDetails?.loanInformation?.requiredInterestReserveMonths ?? "";
  const nextRehabBudget = tempLoanDetails?.loanEconomics?.totalRehabBudget ?? "";
  return (
    prevReserve !== nextReserve ||
    prevReserveMonths !== nextReserveMonths ||
    prevRehabBudget !== nextRehabBudget
  );
}

// The immigrationStatus field can invalidate a couple of previously-set
// credit-score errors; both checks are kept exactly as before, just moved
// out of handleInputChange's body.
function clearImmigrationStatusErrors(
  tempFormErrors: ObjectType,
  subComponentLabel: string,
  field: any,
  tempValue: any,
  checkComponent: string,
): void {
  if (
    subComponentLabel === "borrowerInformation" &&
    field.label === "immigrationStatus"
  ) {
    if (
      tempValue === "Foriegn National" &&
      tempFormErrors?.[subComponentLabel]?.[checkComponent]
    ) {
      delete tempFormErrors[subComponentLabel][checkComponent];
    }
  }

  if (
    subComponentLabel === "borrowerInformation" &&
    field.label === "immigrationStatus"
  ) {
    if (
      tempValue === "Foriegn National" &&
      tempFormErrors[subComponentLabel]["creditScoreActual"]
    ) {
      delete tempFormErrors[subComponentLabel]["creditScoreActual"];
    }
  }
}

function updateLoanFormErrorsForField(
  tempFormErrors: ObjectType,
  subComponentLabel: string,
  field: any,
  error: any,
  translation: any,
): ObjectType {
  if (!error && tempFormErrors?.[subComponentLabel]?.[field?.label]) {
    delete tempFormErrors[subComponentLabel][field.label];
    return tempFormErrors;
  }
  return {
    ...tempFormErrors,
    [subComponentLabel]: {
      ...tempFormErrors[subComponentLabel],
      [field.label]: translation(error),
    },
  };
}

function buildPropFormErrorForField(
  tempPropFormError: any,
  propDetail: ObjectType,
  subComponentLabel: string,
  field: any,
  unitIdx: number | undefined,
  error: any,
  translation: any,
): any {
  if (!isFalsyButNotZero(unitIdx)) {
    return {
      ...tempPropFormError,
      [subComponentLabel]: {
        ...tempPropFormError[subComponentLabel],
        [field.label]: translation(error),
      },
    };
  }
  const unitInformations: ObjectType[] = [];
  propDetail?.[subComponentLabel]?.map(
    (unitInfo: ObjectType, unitInfoIdx: number) => {
      if (unitInfoIdx === unitIdx) {
        unitInformations[unitInfoIdx] = {
          [field.label]: translation(error),
        };
      }
    },
  );
  return {
    ...tempPropFormError,
    [subComponentLabel]: [...unitInformations],
  };
}

// Mutates tempPropFormErrors in place (same behavior as the original inline
// map), covering both the "clear a resolved error" and "record a new error"
// paths for property-level fields.
function updatePropertyFormErrorsForField(
  tempPropertyDetails: ObjectType[],
  tempPropFormErrors: ObjectType[],
  error: any,
  subComponentLabel: string,
  field: any,
  childIdx: number | undefined,
  unitIdx: number | undefined,
  translation: any,
): void {
  tempPropertyDetails.forEach((propDetail: ObjectType, idx: number) => {
    if (!error) {
      if (
        tempPropFormErrors &&
        tempPropFormErrors[idx]?.[subComponentLabel]?.[field?.label]
      ) {
        delete tempPropFormErrors[idx]?.[subComponentLabel]?.[field.label];
      } else if (
        isFalsyButNotZero(unitIdx) &&
        tempPropFormErrors[idx]?.[subComponentLabel]?.[unitIdx as number]?.[
          field.label
        ]
      ) {
        delete tempPropFormErrors[idx]?.[subComponentLabel]?.[
          unitIdx as number
        ][field.label];
      }
    } else if (idx === childIdx) {
      let tempPropFormError: any = tempPropFormErrors[idx] || {};
      if (idx === childIdx) {
        tempPropFormError = buildPropFormErrorForField(
          tempPropFormError,
          propDetail,
          subComponentLabel,
          field,
          unitIdx,
          error,
          translation,
        );
      }
      tempPropFormErrors[idx] = tempPropFormError;
    }
  });
}

// Shared by the two exitStrategy side effects below: clears a single
// propertyEconomics field on every property, unless a specific unit is
// being edited.
function clearPropertyEconomicsFieldIfNoUnit(
  tempPropertyDetails: ObjectType[],
  unitIdx: number | undefined,
  economicsFieldName: string,
): ObjectType[] {
  return tempPropertyDetails.map((propDetail: ObjectType) => {
    const tempPropDetail = { ...propDetail };
    if (!isFalsyButNotZero(unitIdx)) {
      return {
        ...tempPropDetail,
        ["propertyEconomics"]: {
          ...tempPropDetail["propertyEconomics"],
          [economicsFieldName]: "",
        },
      };
    }
    return tempPropDetail;
  });
}

// borrowerAddress / interestOnlyFlag / borrowerGuarantor overrides: fields
// whose side effect is purely a function of the field just edited.
function applyLoanDetailsIdentityFieldOverrides(
  tempLoanDetails: any,
  subComponentLabel: string,
  field: any,
  tempValue: any,
): any {
  let updatedLoanDetails = tempLoanDetails;
  if (field.id === "borrowerAddress") {
    updatedLoanDetails = {
      ...updatedLoanDetails,
      [subComponentLabel]: {
        ...updatedLoanDetails[subComponentLabel],
        ["borrowerAddress"]: `${tempValue?.street_line ?? ""} ${tempValue?.city ?? ""} ${tempValue?.state ?? ""} ${tempValue?.zipcode ?? ""}`,
        city: tempValue?.city ?? "",
        state: tempValue?.state ?? "",
        zipcode: tempValue?.zipcode ?? "",
      },
    };
  }
  if (
    ["interestOnlyFlag", "borrowerGuarantor"].includes(field?.label) &&
    tempValue === "No"
  ) {
    const label =
      field.label === "interestOnlyFlag" ? "ioPeriod" : "immigrationStatus";
    updatedLoanDetails = {
      ...updatedLoanDetails,
      [subComponentLabel]: {
        ...updatedLoanDetails[subComponentLabel],
        [label]: "",
      },
    };
  }
  return updatedLoanDetails;
}

// loanPurpose / numberOfProperties / crossCollaterlization overrides.
function applyLoanDetailsDependentFieldOverrides(
  tempLoanDetails: any,
  subComponentLabel: string,
  field: any,
  tempValue: any,
): any {
  let updatedLoanDetails = tempLoanDetails;
  if (field.label === "loanPurpose" && tempValue === "Purchase") {
    updatedLoanDetails = {
      ...updatedLoanDetails,
      [subComponentLabel]: {
        ...updatedLoanDetails[subComponentLabel],
        cashOutFlag: "No Cash Out",
      },
    };
  }
  if (field.label === "numberOfProperties") {
    updatedLoanDetails = {
      ...updatedLoanDetails,
      [subComponentLabel]: {
        ...updatedLoanDetails[subComponentLabel],
        crossCollaterlization: tempValue <= 1 ? "N" : "Y",
        predominantPropertyType: null,
        predominantState: "",
      },
    };
  }
  if (["crossCollaterlization"].includes(field?.label) && tempValue === "N") {
    updatedLoanDetails = {
      ...updatedLoanDetails,
      [subComponentLabel]: {
        ...updatedLoanDetails[subComponentLabel],
        ["predominantPropertyType"]: "",
        ["predominantState"]: "",
      },
    };
  }
  return updatedLoanDetails;
}

// The two exitStrategy-driven propertyEconomics resets.
function applyExitStrategyPropertyEconomicsEffects(
  tempPropertyDetails: any,
  field: any,
  tempValue: any,
  unitIdx: number | undefined,
  chipsData: string[],
): any {
  let updatedPropertyDetails = tempPropertyDetails;
  if (field.label === "exitStrategy" && tempValue !== "Rehab to Hold/Rent") {
    updatedPropertyDetails = clearPropertyEconomicsFieldIfNoUnit(
      updatedPropertyDetails,
      unitIdx,
      "estimatedRentPerYearUponCompletion",
    );
  }
  if (
    field.label === "exitStrategy" &&
    tempValue !== "Build to Rent" &&
    chipsData.includes("Ground Up")
  ) {
    updatedPropertyDetails = clearPropertyEconomicsFieldIfNoUnit(
      updatedPropertyDetails,
      unitIdx,
      "grossPotentialRentMonthly",
    );
  }
  return updatedPropertyDetails;
}

// All of the loanDetails/borrowerDetails-specific side effects that used to
// live inline in handleInputChange, now split across three focused helpers.
// The relative order between the loanDetails-only overrides and the
// property-economics overrides doesn't affect the result (they touch
// disjoint pieces of state), so this orchestration is behavior-preserving.
function applyLoanDetailsFieldSideEffects(
  tempLoanDetails: any,
  tempPropertyDetails: any,
  subComponentLabel: string,
  field: any,
  tempValue: any,
  unitIdx: number | undefined,
  chipsData: string[],
): { tempLoanDetails: any; tempPropertyDetails: any } {
  let updatedLoanDetails = {
    ...tempLoanDetails,
    [subComponentLabel]: {
      ...tempLoanDetails[subComponentLabel],
      [field.label]: tempValue,
    },
  };

  updatedLoanDetails = applyLoanDetailsIdentityFieldOverrides(
    updatedLoanDetails,
    subComponentLabel,
    field,
    tempValue,
  );
  updatedLoanDetails = applyLoanDetailsDependentFieldOverrides(
    updatedLoanDetails,
    subComponentLabel,
    field,
    tempValue,
  );
  const updatedPropertyDetails = applyExitStrategyPropertyEconomicsEffects(
    tempPropertyDetails,
    field,
    tempValue,
    unitIdx,
    chipsData,
  );

  return {
    tempLoanDetails: updatedLoanDetails,
    tempPropertyDetails: updatedPropertyDetails,
  };
}

function applyUnitFieldUpdate(
  propDetail: ObjectType,
  subComponentLabel: string,
  field: any,
  unitIdx: number | undefined,
  tempValue: any,
): ObjectType {
  if (!propDetail?.[subComponentLabel]) {
    return {
      ...propDetail,
      [subComponentLabel]: [{ [field.label]: tempValue }],
    };
  }
  const unitInformations = propDetail?.[subComponentLabel]?.map(
    (unitInfo: ObjectType, unitInfoIdx: number) => {
      if (unitInfoIdx === unitIdx) {
        return {
          ...unitInfo,
          [field.label]: tempValue,
        };
      }
      return unitInfo;
    },
  );
  return {
    ...propDetail,
    [subComponentLabel]: [...unitInformations],
  };
}

// Used for the non loanDetails/borrowerDetails branch (i.e. property-level
// fields): computes the updated propDetail for a single property, leaving
// every property other than childIdx untouched.
function computeUpdatedPropertyDetailForField(
  propDetail: ObjectType,
  idx: number,
  childIdx: number | undefined,
  field: any,
  subComponentLabel: string,
  tempValue: any,
  unitIdx: number | undefined,
  fetchData: any,
  chipsData: string[],
  setChipsData: any,
): ObjectType {
  if (idx !== childIdx) {
    return propDetail;
  }

  let indexOfMostRestrictiveProperty = 0;
  if (
    fetchData?.autoCalculatedLoanResults &&
    fetchData?.propertyDetails?.length
  ) {
    indexOfMostRestrictiveProperty =
      fetchData?.autoCalculatedLoanResults?.loanCharacterisation
        ?.indexOfMostRestrictiveProperty ?? 0;
  }
  // if (childIdx === 0) {
  if (
    childIdx === indexOfMostRestrictiveProperty &&
    field.label === "propertyType"
  ) {
    updateChipsData({ ...fetchData }, chipsData, setChipsData, tempValue);
    // delete tempPropFormErrors[idx]?.[subComponentLabel]?.[
    //   "numberOfUnits"
    // ];
    return {
      ...propDetail,
      ["propertyInformation"]: {
        ...propDetail["propertyInformation"],
        ["propertyType"]: tempValue,
        // ["numberOfUnits"]: "",
      },
    };
  }
  if (!isFalsyButNotZero(unitIdx)) {
    return {
      ...propDetail,
      [subComponentLabel]: {
        ...propDetail[subComponentLabel],
        [field.label]: tempValue,
      },
    };
  }
  return applyUnitFieldUpdate(
    propDetail,
    subComponentLabel,
    field,
    unitIdx,
    tempValue,
  );
}

// A Credit-Committee (or any update_pricing-permitted user)'s manually
// entered interestRate/finalToorakPrice (see LoanEconomics.ts) is only valid
// against the loan data it was entered for — editing any OTHER field
// invalidates it. Mirrors PRICING_OVERRIDE_FIELDS/PRICING_OVERRIDE_SECTION
// in useLoanSizingHandler.tsx (the v2 flow), ported here for v1 since this
// function has no shared hook state to reuse.
const PRICING_OVERRIDE_FIELDS = ["interestRate", "finalToorakPrice"];
const PRICING_OVERRIDE_SECTION = "loanEconomics";

// Clears any stale pricing-override fields in place, on tempLoanDetails,
// unless the field currently being edited is itself one of the override
// fields — synchronous and self-contained (no setState involved) so the
// mutation is guaranteed to have happened before tempLoanDetails is handed
// to setLoanDetails later in handleInputChange. Returns the keys actually
// cleared, so the caller can fold matching audit "cleared" entries into its
// own setPendingAuditEdits update.
function clearStalePricingOverrides(
  tempLoanDetails: ObjectType,
  subComponentLabel: string,
  fieldId: string,
): string[] {
  const isEditingPricingOverrideField =
    subComponentLabel === PRICING_OVERRIDE_SECTION &&
    PRICING_OVERRIDE_FIELDS.includes(fieldId);
  if (isEditingPricingOverrideField) return [];

  const staleOverrideKeys = PRICING_OVERRIDE_FIELDS.filter(
    (key) => tempLoanDetails?.loanEconomics?.[key],
  );
  if (!staleOverrideKeys.length) return [];

  tempLoanDetails.loanEconomics = {
    ...tempLoanDetails.loanEconomics,
    ...Object.fromEntries(staleOverrideKeys.map((key) => [key, ""])),
  };
  return staleOverrideKeys;
}

export function handleInputChange(
  value: any,
  field: any,
  subComponentLabel: string,
  translation: any,
  loanContextData: ObjectType,
  component?: ObjectType | undefined,
  childIdx?: number | undefined,
  unitIdx?: number,
  isCurrency?: boolean,
  isPercentage?: boolean,
) {
  const {
    loanFormErrors,
    propFormErrors,
    loanDetails,
    propertyDetails,
    setLoanFormErrors,
    setPropFormErrors,
    setLoanDetails,
    setPropertyDetails,
    setLoanDetailsConfig,
    setPropertyDetailsConfig,
    setBorrowerInformationConfig,
    setDebouncedValue,
    chipsData,
    setChipsData,
    fetchData,
    setPendingAuditEdits,
  } = loanContextData;

  const auditPath = getAuditFieldPath(
    subComponentLabel,
    field.id ?? field.label,
    childIdx,
    unitIdx,
  );
  const tempValue = value;
  let tempFormErrors: ObjectType = { ...loanFormErrors };
  const tempPropFormErrors: ObjectType[] = [...(propFormErrors ?? [])];

  let tempLoanDetails = JSON.parse(JSON.stringify(loanDetails ?? {}));
  let tempPropertyDetails = JSON.parse(JSON.stringify(propertyDetails ?? []));
  const checkComponent = tempFormErrors?.[subComponentLabel]?.[
    "creditScoreActual"
  ]
    ? "creditScoreActual"
    : "originalCreditScore";

  clearImmigrationStatusErrors(
    tempFormErrors,
    subComponentLabel,
    field,
    tempValue,
    checkComponent,
  );

  if (field.required) {
    const error = field?.getFieldErrors(value);

    if (["loanDetails", "borrowerDetails"].includes(component?.label)) {
      tempFormErrors = updateLoanFormErrorsForField(
        tempFormErrors,
        subComponentLabel,
        field,
        error,
        translation,
      );
    } else {
      updatePropertyFormErrorsForField(
        tempPropertyDetails,
        tempPropFormErrors,
        error,
        subComponentLabel,
        field,
        childIdx,
        unitIdx,
        translation,
      );
    }
  }

  if (["loanDetails", "borrowerDetails"].includes(component?.label)) {
    ({ tempLoanDetails, tempPropertyDetails } = applyLoanDetailsFieldSideEffects(
      tempLoanDetails,
      tempPropertyDetails,
      subComponentLabel,
      field,
      tempValue,
      unitIdx,
      chipsData,
    ));
  } else {
    tempPropertyDetails = (tempPropertyDetails ?? []).map(
      (propDetail: ObjectType, idx: number) =>
        computeUpdatedPropertyDetailForField(
          propDetail,
          idx,
          childIdx,
          field,
          subComponentLabel,
          tempValue,
          unitIdx,
          fetchData,
          chipsData,
          setChipsData,
        ),
    );
  }

  // Mutates tempLoanDetails synchronously (before it's handed to
  // setLoanDetails below) — must not be deferred into the setPendingAuditEdits
  // updater, since that callback only runs later (whenever React processes
  // the queued update), not at the point this line executes.
  const clearedPricingOverrideKeys = clearStalePricingOverrides(
    tempLoanDetails,
    subComponentLabel,
    field.id ?? field.label,
  );

  setPendingAuditEdits?.((prevEdits: ObjectType) => {
    const nextEdits: ObjectType = {
      ...prevEdits,
      [auditPath]: { value, comment: DEFAULT_AUDIT_COMMENT },
    };
    clearedPricingOverrideKeys.forEach((key) => {
      nextEdits[getAuditFieldPath(PRICING_OVERRIDE_SECTION, key)] = {
        value: "",
        comment: DEFAULT_AUDIT_COMMENT,
      };
    });
    return nextEdits;
  });

  const dynamicFields = getDynamicFields(
    value,
    field,
    subComponentLabel,
    loanContextData,
    component,
    childIdx,
    unitIdx,
    chipsData,
    tempValue,
  );

  setLoanDetailsConfig(dynamicFields?.tempLoanDetailsConfig);
  setPropertyDetailsConfig(dynamicFields?.tempPropertyDetailsConfig);
  setBorrowerInformationConfig(dynamicFields?.tempBorrowerInformationConfig);
  setPropertyDetails(tempPropertyDetails);
  setLoanDetails(tempLoanDetails);
  setLoanFormErrors(tempFormErrors);
  setPropFormErrors(tempPropFormErrors);
  postGuidelineEvaluation(setDebouncedValue, value);
}

export function handleIconButtonClick(
  label: string,
  index: number,
  loanContextData: ObjectType,
  unitIdx?: number,
  propertyIdx?: number,
) {
  const {
    defaultSelectedDataView,
    propertyDetails,
    propertyDetailsConfig,
    propertyDetailTemplate,
    propertyDetailsConfigTemplate,
    unitInfoConfigTemplate,
    unitInfoTemplate,
    autoCalculatedFields,
    setPropertyDetails,
    setPropertyDetailsConfig,
    setDebouncedValue,
    setAutoCalculatedFields,
    chipsData,
    setChipsData,
    fetchData,
    loanDetails,
  } = loanContextData;

  let tempPropertyDetailsConfig = [...propertyDetailsConfig];
  let tempPropertyDetails = JSON.parse(JSON.stringify(propertyDetails));
  const tempAutoCalculatedFields = { ...autoCalculatedFields };

  let newPropertyType: string;

  switch (label) {
    case "deleteProperty":
      tempPropertyDetails = tempPropertyDetails.filter(
        (propDetails: ObjectType, idx: number) => idx !== index,
      );
      tempPropertyDetailsConfig = tempPropertyDetailsConfig.filter(
        (propDetails: ObjectType, idx: number) => idx !== index,
      );
      newPropertyType =
        tempPropertyDetails[0]?.["propertyInformation"]["propertyType"];
      updateChipsData(fetchData, chipsData, setChipsData, newPropertyType);
      break;
    case "addProperty":
      // Deep-clone the template — pushing the shared reference would leave
      // every newly-added property pointing at the exact same object (incl.
      // propertyLocation), so editing one before its address is resolved via
      // autocomplete would silently edit all of them.
      tempPropertyDetails.push(JSON.parse(JSON.stringify(propertyDetailTemplate)));
      tempPropertyDetailsConfig.push({
        ...propertyDetailsConfigTemplate,
        fields: propertyDetailsConfigTemplate?.fields?.map((field: any) => {
          if (
            ["propertyInformation", "propertyEconomics"].includes(field?.id)
          ) {
            const isBuildToRent =
              loanDetails?.loanInformation?.exitStrategy === "Build to Rent";
            const isRehabToHoldRent =
              loanDetails?.loanInformation?.exitStrategy ===
              "Rehab to Hold/Rent";
            const isNotFullDataView =
              defaultSelectedDataView !== "fullDataView";

            return {
              ...field,
              fields: field.fields.map((subField: any) => {
                switch (subField.id) {
                  case "monthlyPropertyGrossRent":
                  case "rentalCharacterization":
                    return {
                      ...subField,
                      minimumDataView: isNotFullDataView,
                      isVisible: isNotFullDataView,
                    };
                  case "grossPotentialRentMonthly":
                    return {
                      ...subField,
                      minimumDataView: isBuildToRent,
                      isVisible: isBuildToRent,
                    };
                  case "estimatedRentPerYearUponCompletion":
                    return {
                      ...subField,
                      minimumDataView: isRehabToHoldRent,
                      // isVisible: isRehabToHoldRent,
                    };
                  default:
                    return subField;
                }
              }),
            };
          }

          return field;
        }),
      });
      if (tempAutoCalculatedFields) {
        tempAutoCalculatedFields?.fields?.forEach((field: any) => {
          if (Array.isArray(field)) {
            field.push(field[0]);
          }
        });
      }
      break;
    case "addMore":
      tempPropertyDetailsConfig = tempPropertyDetailsConfig?.map(
        (propConfig: ObjectType, idx: number) => {
          if (idx === propertyIdx) {
            const tempConfigField = propConfig?.fields?.map(
              (field: ObjectType) => {
                if (Array.isArray(field)) {
                  return [...field, unitInfoConfigTemplate];
                }
                return field;
              },
            );
            return { ...propConfig, fields: tempConfigField };
          }
          return propConfig;
        },
      );

      tempPropertyDetails = tempPropertyDetails?.map(
        (propConfig: ObjectType, idx: number) => {
          if (idx === propertyIdx) {
            return {
              ...propConfig,
              unitInformation: [
                ...(propConfig?.unitInformation ?? {}),
                unitInfoTemplate[0],
              ],
            };
          }
          return propConfig;
        },
      );

      break;
    case "delete":
      tempPropertyDetailsConfig = tempPropertyDetailsConfig.map(
        (propConfig: ObjectType, idx: number) => {
          if (idx === propertyIdx) {
            const tempConfigField = propConfig?.fields?.map(
              (field: ObjectType) => {
                if (Array.isArray(field)) {
                  return field.filter(
                    (unitField: ObjectType, unitFieldIdx: number) =>
                      unitFieldIdx !== unitIdx,
                  );
                }
                return field;
              },
            );
            return { ...propConfig, fields: tempConfigField };
          }
          return propConfig;
        },
      );

      tempPropertyDetails = tempPropertyDetails.map(
        (propConfig: ObjectType, idx: number) => {
          if (idx === propertyIdx) {
            return {
              ...propConfig,
              unitInformation: propConfig?.unitInformation.filter(
                (unitInfo: ObjectType, unitInfoIdx: number) =>
                  unitIdx !== unitInfoIdx,
              ),
            };
          }
          return propConfig;
        },
      );
      break;
    default:
      break;
  }

  postGuidelineEvaluation(setDebouncedValue, tempPropertyDetails);
  setPropertyDetails(tempPropertyDetails);
  setPropertyDetailsConfig(tempPropertyDetailsConfig);
  setAutoCalculatedFields(tempAutoCalculatedFields);
}

// Resolves the effective value of an auto-calculated field for the
// fieldsToRender filter below. Mirrors the original nested if/else exactly.
function resolveAutoCalculatedFieldValue(
  field: any,
  subComponent: any,
  isAutoCalculatedObj: ObjectType | undefined,
  autoCalculatedLoanResults: any,
  autoCalculatedPropResults: any,
  propIdx: any,
  childIdx: number | undefined,
  unitIdx: number | undefined,
): any {
  if (isAutoCalculatedObj?.isAutoCalculated) {
    if (!isAutoCalculatedObj?.propLevel) {
      return autoCalculatedLoanResults?.[subComponent?.label]?.[field?.id];
    }
    return autoCalculatedPropResults[propIdx as number]?.[
      subComponent?.label
    ]?.[field?.label];
  }
  if (autoCalculatedLoanResults?.[subComponent?.label]?.[field?.id]) {
    return autoCalculatedLoanResults?.[subComponent?.label]?.[field?.id];
  }
  if (!isFalsyButNotZero(unitIdx)) {
    return autoCalculatedPropResults[childIdx as number]?.[
      subComponent.label
    ]?.[field.label];
  }
  return autoCalculatedPropResults.length &&
    autoCalculatedPropResults[childIdx as number]?.[subComponent.label]?.length
    ? autoCalculatedPropResults[childIdx as number]?.[subComponent.label][
        unitIdx as number
      ]?.[field.label]
    : "";
}

// Only called once a field is known to be auto-calculated: decides whether
// an auto-calculated field with no resolvable value should still render,
// based on the subComponent exceptions list.
function shouldIncludeAutoCalculatedField(
  subComponent: any,
  value: any,
): boolean {
  const tempExceptions = [
    "loanFeatures",
    "loanCharacterisation",
    "marketData",
    "subMarketData",
  ];
  if (
    !isFalsyButNotZero(value) &&
    !tempExceptions.includes(subComponent.label)
  ) {
    return false;
  }
  return true;
}

export function renderSubComponentFields(
  subComponent: any,
  propIdx: any,
  data: any,
  component: ObjectType,
  loanType: string,
  theme: any,
  isApplicationSubmitted: boolean,
  translation: any,
  loanContextData: ObjectType,
  childIdx?: number | undefined,
  unitIdx?: number,
  isAutoCalculatedObj?: ObjectType,
) {
  const {
    defaultSelectedDataView,
    expandLeverageAndTests,
    autoCalculatedLoanResults,
    autoCalculatedPropResults,
  } = loanContextData;
  const subComponentFields: any = subComponent?.fields?.filter((ele: any) => {
    if (defaultSelectedDataView === "fullDataView" && ele?.isVisible) {
      return ele;
    }
    return ele[defaultSelectedDataView] && ele?.isVisible;
  });

  const subLeftFields = subComponent?.fields?.filter(
    (ele: any) => ele.isExpandedView,
  );

  const fieldsToRender = subComponentFields?.filter((field: any) => {
    const isFieldAutoCalculated =
      field?.isAutoCalculated &&
      field?.isAutoCalculated(loanType, defaultSelectedDataView);
    if (!isFieldAutoCalculated) {
      return true;
    }
    const value = resolveAutoCalculatedFieldValue(
      field,
      subComponent,
      isAutoCalculatedObj,
      autoCalculatedLoanResults,
      autoCalculatedPropResults,
      propIdx,
      childIdx,
      unitIdx,
    );
    return shouldIncludeAutoCalculatedField(subComponent, value);
  });

  return !expandLeverageAndTests ? (
    <div>
      <Grid container>
        {fieldsToRender?.map((field: any, idx: number) => {
          return (
            <Grid
              key={idx}
              item
              xs={field.columnSplit}
              className={idx < 2 ? "" : "mt-32"}
            >
              {renderFieldUI(
                field,
                idx,
                data?.[field.label],
                handleInputChange,
                loanType,
                theme,
                isApplicationSubmitted,
                translation,
                loanContextData,
                subComponent.label,
                component,
                childIdx,
                unitIdx,
                { ...isAutoCalculatedObj, propIdx },
              )}
            </Grid>
          );
        })}
      </Grid>
    </div>
  ) : (
    <div>
      <Grid container>
        {subLeftFields?.map((field: any, idx: number) => {
          return (
            <Grid key={idx} item sx={{ margin: "0 !important" }}>
              {renderFieldUI(
                field,
                idx,
                data?.[field.label],
                handleInputChange,
                loanType,
                theme,
                isApplicationSubmitted,
                translation,
                loanContextData,
                subComponent.label,
                component,
                childIdx,
                unitIdx,
              )}
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
}

export function renderFieldUI(
  field: any,
  idx: any,
  value: any,
  handleOnChange: any,
  loanType: string | undefined,
  theme: any,
  isApplicationSubmitted: boolean,
  translation: any,
  loanContextData: ObjectType,
  subComponentLabel?: any,
  component?: ObjectType,
  childIdx?: number,
  unitIdx?: number,
  autoCalculatedData?: ObjectType,
) {
  const {
    defaultSelectedDataView,
    autoCalculatedLoanResults,
    autoCalculatedPropResults,
    loanFormErrors,
    propFormErrors,
    addressOptions,
    borrowerAddressOptions,
    zipCodeOptions,
    borrowerAddressInputValue,
    setBorrowerAddressInputValue,
    setAddressInputValue,
    addressInputValue,
    zipCodeInputValue,
    setZipCodeInputValue,
  } = loanContextData;

  if (
    field?.isAutoCalculated &&
    field?.isAutoCalculated(loanType, defaultSelectedDataView)
  ) {
    if (autoCalculatedData?.isAutoCalculated) {
      if (!autoCalculatedData?.propLevel) {
        value = autoCalculatedLoanResults?.[subComponentLabel]?.[field?.id];
      } else {
        value =
          autoCalculatedPropResults[autoCalculatedData?.propIdx as number]?.[
            subComponentLabel
          ]?.[field?.label];
      }
    } else if (autoCalculatedLoanResults?.[subComponentLabel]?.[field?.id]) {
      value = autoCalculatedLoanResults?.[subComponentLabel]?.[field?.id];
    } else {
      if (!isFalsyButNotZero(unitIdx)) {
        value =
          autoCalculatedPropResults[childIdx as number]?.[subComponentLabel]?.[
            field.label
          ];
      } else {
        value =
          autoCalculatedPropResults.length &&
          autoCalculatedPropResults[childIdx as number]?.[subComponentLabel]
            ?.length
            ? autoCalculatedPropResults[childIdx as number]?.[
                subComponentLabel
              ][unitIdx as number]?.[field.label]
            : "";
      }
    }
  }

  let errorMsg = "";
  if (["loanDetails", "borrowerDetails"].includes(component?.label)) {
    errorMsg = loanFormErrors?.[subComponentLabel]?.[field.label] ?? "";
  } else if (isFalsyButNotZero(childIdx)) {
    if (!isFalsyButNotZero(unitIdx)) {
      errorMsg =
        propFormErrors?.[childIdx as number]?.[subComponentLabel]?.[
          field.label
        ];
    } else {
      errorMsg =
        propFormErrors?.[childIdx as number]?.[subComponentLabel]?.[
          unitIdx as number
        ]?.[field.label];
    }
  }

  switch (field?.type(loanType, defaultSelectedDataView)) {
    case "buttonGroup":
      return (
        <>
          {getTitleInfo(field, false, loanType, translation, loanContextData, { section: subComponentLabel, propertyId: childIdx, unitId: unitIdx })}
          <div className="mt-4">
            <CustomButtonGroup
              buttons={field.data}
              selectedButton={value}
              field={field}
              handleDataViewChange={handleOnChange}
              subComponentLabel={subComponentLabel}
              className="button-grp-style"
              buttonSize="large"
              translation={translation}
              loanContextData={loanContextData}
              theme={theme}
              errorMsg={errorMsg ?? ""}
              isDataView={false}
              component={component}
              childIdx={childIdx}
              unitIdx={unitIdx}
              isApplicationSubmitted={isApplicationSubmitted}
            />
          </div>
        </>
      );
    case "dropDown":
      return (
        <>
          {getTitleInfo(field, false, loanType, translation, loanContextData, { section: subComponentLabel, propertyId: childIdx, unitId: unitIdx })}
          <div className="mt-4">
            <CustomSelect
              data={field?.data ?? []}
              field={field}
              value={value ?? ""}
              onChange={handleOnChange}
              subComponentLabel={subComponentLabel}
              theme={theme}
              component={component}
              translation={translation}
              loanContextData={loanContextData}
              childIdx={childIdx}
              unitIdx={unitIdx}
              errorMsg={errorMsg ?? ""}
              defaultSelectedDataView={defaultSelectedDataView}
              loanType={loanType}
              checkEligibility={field.checkEligibility}
              isApplicationSubmitted={isApplicationSubmitted}
            />
          </div>
        </>
      );
    case "string":
    case "number":
      return (
        <>
          {getTitleInfo(field, false, loanType, translation, loanContextData, { section: subComponentLabel, propertyId: childIdx, unitId: unitIdx })}
          <div className="mt-4">
            <CustomInput
              type={field?.type}
              field={field}
              value={value ?? ""}
              onChange={handleOnChange}
              subComponentLabel={subComponentLabel}
              theme={theme}
              component={component}
              translation={translation}
              loanContextData={loanContextData}
              childIdx={childIdx}
              unitIdx={unitIdx}
              errorMsg={errorMsg ?? ""}
              isCurrency={field.isCurrency}
              isPercentage={field.isPercentage}
              defaultSelectedDataView={defaultSelectedDataView}
              loanType={loanType}
              isApplicationSubmitted={isApplicationSubmitted}
            />
          </div>
        </>
      );
    case "date":
      return (
        <>
          {getTitleInfo(field, false, loanType, translation, loanContextData, { section: subComponentLabel, propertyId: childIdx, unitId: unitIdx })}
          <div className="mt-4">
            <CustomDatePicker
              field={field}
              value={value}
              onChange={handleOnChange}
              subComponentLabel={subComponentLabel}
              theme={theme}
              component={component}
              translation={translation}
              loanContextData={loanContextData}
              childIdx={childIdx}
              unitIdx={unitIdx}
              errorMsg={errorMsg ?? ""}
              isApplicationSubmitted={isApplicationSubmitted}
            />
          </div>
        </>
      );
    case "autoComplete":
      let options = [];
      let selectedValue = value;
      let getOptionLabels: any = getAddressOptionLabel;
      let onChange: any = addressOnChange;
      let inputValue = "";         // ← no longer tied to `value`
      let onInputChange: any = addressPostalCodeOnInputChange;

      if (field.id === "borrowerAddress") {
        options = borrowerAddressOptions;
        onChange = handleOnChange;
        inputValue = borrowerAddressInputValue || selectedValue;
      } else if (field.id === "address") {
        options = addressOptions;
        getOptionLabels = getAddressOptionLabel;
        inputValue = addressInputValue[childIdx ?? 0];
      } else {
        options = zipCodeOptions;
        getOptionLabels = getPostalOptionLabel;
        onChange = postalOnChange;
        inputValue = zipCodeInputValue[childIdx ?? 0];
      }
      return (
        <>
          {getTitleInfo(field, true, loanType, translation, loanContextData, { section: subComponentLabel, propertyId: childIdx, unitId: unitIdx })}
          <div className="mt-4">
            <CustomAutoComplete
              label={translation(field?.label)}
              options={options}
              selectedValue={selectedValue}
              onChange={onChange}
              subComponentLabel={subComponentLabel}
              getOptionLabels={getOptionLabels}
              inputValue={inputValue}
              onInputChange={onInputChange}
              errorMsg={errorMsg ?? ""}
              isAutoPopulated={field.isAutoPopulated}
              component={component}
              childIdx={childIdx}
              unitIdx={unitIdx}
              isApplicationSubmitted={isApplicationSubmitted}
              field={field}
              loanContextData={loanContextData}
              translation={translation}
            />
          </div>
        </>
      );
    case "radio":
      return (
        <>
          {getTitleInfo(field, true, loanType, translation, loanContextData, { section: subComponentLabel, propertyId: childIdx, unitId: unitIdx })}
          <div className="mt-4">
            <CustomRadio
              field={field}
              options={field?.data}
              value={value}
              handleChange={handleCondoEligibilityModalChange}
              loanContextData={loanContextData}
              component={component}
              childIdx={childIdx}
              unitIdx={unitIdx}
              errorMsg={errorMsg ?? ""}
              isApplicationSubmitted={isApplicationSubmitted}
            />
          </div>
        </>
      );
    case "checkBox":
      return (
        <div className="mt-4">
          <CheckBox
            label={getTitleInfo(
              field,
              false,
              loanType,
              translation,
              loanContextData,
              { section: subComponentLabel, propertyId: childIdx, unitId: unitIdx },
            )}
            field={field}
            value={value}
            handleChange={handleOnChange}
            subComponentLabel={subComponentLabel}
            component={component}
            translation={translation}
            loanContextData={loanContextData}
            childIdx={childIdx}
            unitIdx={unitIdx}
            errorMsg={errorMsg ?? ""}
            isApplicationSubmitted={isApplicationSubmitted}
          />
        </div>
      );
    default:
      break;
  }
}

export function renderSubComponentUI(
  subComponent: any,
  idx: any,
  data: any,
  component: ObjectType,
  loanType: string,
  theme: any,
  translation: any,
  loanContextData: ObjectType,
  childIdx?: number,
  isAutoCalculatedObj?: ObjectType,
) {
  const {
    defaultSelectedDataView,
    propertyDetails,
    loanDetails,
    expandLeverageAndTests,
    setIsUnitExpanded,
    isUnitExpanded,
    autoCalculatedLoanResults,
    autoCalculatedPropResults,
  } = loanContextData;

  const showUnitInfo = subComponent[0]?.fields?.filter(
    (tempComponent: ObjectType) => {
      if (defaultSelectedDataView === "fullDataView") {
        return tempComponent?.isVisible;
      }
      return tempComponent?.minimumDataView && component?.isVisible;
    },
  )?.length;

  if (Array.isArray(subComponent) && showUnitInfo) {
    return (
      <>
        <h4>{translation(subComponent[0]?.label)}</h4>
        {subComponent.map((ele: ObjectType, i: number) => {
          const propLabel: string = `Property #${(childIdx as number) + 1}`;
          const unitLabel: string = `Unit ${(i as number) + 1}`;
          return !expandLeverageAndTests ? (
            <Accordion
              key={i}
              defaultExpanded={true}
              expanded={isUnitExpanded[propLabel]?.[unitLabel]}
              onChange={() =>
                setIsUnitExpanded({
                  ...isUnitExpanded,
                  [propLabel]: {
                    ...isUnitExpanded[propLabel],
                    [unitLabel]: !isUnitExpanded[propLabel]?.[unitLabel],
                  },
                })
              }
            >
              <UnitAccordionSummary>
                <div>
                  <h4>{`${translation("unit")} ${i + 1}`}</h4>
                </div>
                <div>
                  {ele?.buttons ? (
                    <div>
                      {ele?.buttons.map((button: any, idx: number) => {
                        return (
                          <IconButton
                            key={idx}
                            disabled={checkIconButtonDisabled(
                              subComponent,
                              button.label,
                              Boolean(loanDetails?.tcLoanId),
                              propertyDetails.length,
                            )}
                            onClick={() =>
                              handleIconButtonClick(
                                button.label,
                                idx,
                                loanContextData,
                                i,
                                childIdx,
                              )
                            }
                            sx={{
                              borderRadius: "0% !important;",
                            }}
                          >
                            <span style={{ fontSize: "12px" }}>
                              {translation(button.label)}
                            </span>
                            &ensp;
                            <img src={button.icon} alt="button-icon" />
                            &ensp;
                            {idx === 0 ? "| " : null}
                          </IconButton>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </UnitAccordionSummary>
              <AccordionDetails className="mt-12">
                <Grid container>
                  {renderSubComponentFields(
                    ele,
                    idx,
                    data ? data[i] : "",
                    component,
                    loanType,
                    theme,
                    Boolean(loanDetails?.tcLoanId),
                    translation,
                    loanContextData,
                    childIdx,
                    i,
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ) : (
            ""
          );
        })}
      </>
    );
  } else {
    const subComponentFields: any = subComponent?.fields?.filter((ele: any) => {
      if (defaultSelectedDataView === "fullDataView" && ele.isVisible) {
        return ele;
      }
      return ele[defaultSelectedDataView] && ele.isVisible;
    });

    let label = translation(
      subComponentFields?.length ? subComponent?.label : "",
    );

    let tempFields: any = subComponent;
    if (isAutoCalculatedObj?.isAutoCalculated) {
      // if(!['marketData','subMarketData']?.includes(subComponent?.label))
      tempFields = checkIfAutoCalculated(
        subComponent,
        isAutoCalculatedObj?.propLevel,
        autoCalculatedLoanResults,
        autoCalculatedPropResults,
        idx,
      );
      if (!tempFields?.fields?.length) {
        label = "";
      } else if (
        tempFields?.fields?.length &&
        isAutoCalculatedObj?.propLevel &&
        defaultSelectedDataView === "fullDataView"
        // !['marketData','subMarketData']?.includes(subComponent?.label)
      ) {
        label = `${translation(subComponent?.label)} - Property #${idx + 1}`;
      }
    }

    return (
      <>
        {!expandLeverageAndTests ? <h4>{label}</h4> : ""}
        {renderSubComponentFields(
          tempFields,
          idx,
          data,
          component,
          loanType,
          theme,
          Boolean(loanDetails?.tcLoanId),
          translation,
          loanContextData,
          childIdx,
          undefined,
          isAutoCalculatedObj,
        )}
      </>
    );
  }
}

export function checkIconButtonDisabled(
  component: any,
  label: any,
  isApplicationSubmitted: boolean,
  propertyDetailsLength?: number,
) {
  if (label === "addProperty" || label === "addMore") {
    return isApplicationSubmitted;
  }
  if (label === "deleteProperty") {
    return propertyDetailsLength === 1 || isApplicationSubmitted;
  }
  if (label === "delete") {
    return component.length === 1 || isApplicationSubmitted;
  }
  return false;
}

export const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderRadius: "6px",
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

export const UnitAccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<img src={AccordionDownArrow} style={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  background: "rgba(234, 222, 248, 0.50)",
  borderRadius: "6px",
  border: "1px solid #DBE2FF",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(180deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

function checkIfAutoCalculated(
  subComponent: any,
  propLevel: boolean,
  autoCalculatedLoanResults: any,
  autoCalculatedPropResults: any,
  propIdx: number,
) {
  const tempSubComponent = {
    ...subComponent,
    fields: subComponent.fields.filter((field: any) => {
      if (propLevel && Object.keys(field).length) {
        return autoCalculatedPropResults[propIdx as number]?.[
          subComponent.label
        ]?.[field?.id];
      } else {
        return autoCalculatedLoanResults?.[subComponent.label]?.[field?.id];
      }
    }),
  };

  return tempSubComponent;
}
